/**
 * 출정 화면.
 *
 * 여기서 고른 것이 곧 전장에 나가는 명부다. 데려간 병사는 죽으면 돌아오지 않으므로,
 * 무엇을 걸고 나가는지가 숫자로 보여야 한다. 분대 정원, 전투력, 그리고 잃었을 때의
 * 재건 비용을 한 화면에 둔다.
 *
 * **두 칸 편성(퍼스트퀸4 방식).** 화면에 칸이 둘 있고(폰은 위·아래, 넓은 화면은 좌우), 칸마다 탭으로
 * 부대 하나나 병영을 띄운다. 병영도 부대 하나로 친다 — 어느 부대에도 없는 병사와 동료가 있는 곳이다.
 * 카드를 누르면 상세(진급·능력치·옮기기)가 뜨고, 끌어서 다른 칸이나 탭에 놓으면 옮긴다(`press.ts`).
 *
 * 예전에는 편성(부대별 병종 묶음) · 병영(병사 한 줄씩) · 출정 정보의 세 칸이었다. 부대와 병영이 서로
 * 다른 모습이라 같은 병사가 두 곳에서 다르게 보였고, 동료는 병영에 없어 부하 부대를 짜려면 고르기
 * 상자를 찾아야 했다. 플레이한 사람이 시안(편성 시안 v8)에서 고른 모습이 이것이다.
 */
import { TERRITORY_BY_ID } from '../meta/realm.ts';
import { realmSection } from './realm.ts';
import { forecastTraits, isRouteDepth, traitNames, traitsForDepth } from '../sim/traits.ts';
import { MAX_SQUADS } from '../sim/commands.ts';
import {
  ELITE,
  eliteBody,
  BRANCH_NAMES,
  Branch,
  COMPANIONS,
  COMPANION_DEPTHS,
  TROOP_BY_ID,
  rebuildCost,
} from '../sim/troops.ts';
import type { CompanionDef, TroopDef } from '../sim/troops.ts';
import {
  availableCompanions,
  companionLevelOf,
  promote,
  CONSCRIPT_FLOOR,
  recruitPlan,
  isDestitute,
  recruitUpTo,
  RECRUIT_TROOP_COST,
  shortfall,
  SQUAD_SIZE,
  setSoldierLock,
  soldierLevel,
  squadSize,
  appointElite,
  dismissElite,
  eliteCap,
  eliteCheck,
  eliteCost,
  elitesOf,
  isElite,
  wageFor,
  mateRank,
  mateSquadSize,
  startDepths,
  heroLevelOf,
  heroProgress,
  metCompanions,
  RAID_XP_CAP,
  RANKS,
  raidBandsOpen,
  rankFor,
  selectionPower,
  soldierById,
  nextRunSeed,
  buySupply,
  supplyCount,
} from '../meta/army.ts';
import { SUPPLIES, SUPPLY_STOCK_MAX } from '../sim/supplies.ts';
import { supplyIcon } from './supply-icons.ts';
import {
  BRANCH_ORDER,
  batchAffordable,
  batchPromotions,
  branchAffordable,
  branchingPromotions,
  levelProgress,
  promotionOptions,
  rosterOrder,
} from '../meta/roster.ts';
import type { Army, Soldier } from '../meta/army.ts';
import type { Selection } from '../meta/run.ts';
import { salarySteps } from '../meta/run.ts';
import { autoPlan, capOf, capacityOf, evenPlan, freeSeats, moveCompanion, moveSoldier, placeOf, reorderSoldier, restorePlan, seatNew } from '../meta/plan.ts';
import type { MoveResult, Place, Plan } from '../meta/plan.ts';
import { HERO, canEnhanceWorn, loadoutOf } from '../meta/stash.ts';
import { freeSkillPoints } from '../meta/skill.ts';
import { isMounted } from '../sim/equipment.ts';
import { drawUnitPortrait, iconKeyFor, unitIcon } from './unit-icon.ts';
import { dressCompanion, dressLeader } from '../sim/scenario.ts';
import { RAID_BANDS, shieldFor, specForTroop } from '../sim/troops.ts';
import { button, clear, el, withKey } from './dom.ts';
import { captainShort, captainText, commandShort, squadName } from './labels.ts';
import { COMPANION_LORE } from './companion-lore.ts';
import { companionStatCard, leaderStatCard, soldierStatCard } from './stats.ts';
import { hookCard } from './press.ts';
import type { CardDrag } from './press.ts';
import { THEME_LABEL, currentChoice, cycleTheme } from './theme.ts';
import { cycleSound, soundLevel } from '../audio/sound.ts';
import { LEVEL_LABEL, soundChosen } from '../audio/settings.ts';

export interface SortieResult {
  readonly selection: Selection;
  readonly seed: number;
  /** 출발 깊이(지름길). 1이거나 넘은 관문 바로 다음 깊이다(`startDepths`). */
  readonly startDepth: number;
}

/** 병영 줄 세우기. 기본은 명단 순서(`rosterOrder`) — 능력치 화면의 표와 같다. */
type SortKey = 'roster' | 'level' | 'promo';

/** 칸 하나가 기억하는 것. 화면을 다시 그려도 남는다. */
interface Slot {
  place: Place;
  filter: Branch | 'all';
  sort: SortKey;
}


/**
 * 이름 · 막대 · 남은 값 한 줄. 진행은 숫자보다 막대 길이로 먼저 읽힌다(장수 지휘 줄). `label`은 읽기 도구용 이름이다.
 */
function progressRow(name: string, have: number, span: number, rest: string, label: string): HTMLElement {
  const pct = span > 0 ? Math.max(0, Math.min(100, Math.floor((have * 100) / span))) : 100;
  const bar = el(
    'span',
    { class: 'pbar', role: 'progressbar', 'aria-label': label, 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(pct) },
    el('i', {}),
  );
  (bar.firstChild as HTMLElement).style.width = `${pct}%`;
  return el('span', { class: 'prow' }, el('span', { class: 'pn' }, name), bar, el('span', { class: 'pr' }, rest));
}

/** 자물쇠 그림(잠근 병사). */
function lockIcon(): SVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 10 12');
  svg.setAttribute('class', 'lock-ico');
  svg.setAttribute('aria-hidden', 'true');
  const shackle = document.createElementNS(NS, 'path');
  shackle.setAttribute('d', 'M2.5 5.5V3.5a2.5 2.5 0 0 1 5 0v2');
  shackle.setAttribute('fill', 'none');
  shackle.setAttribute('stroke', 'currentColor');
  shackle.setAttribute('stroke-width', '1.4');
  const body = document.createElementNS(NS, 'rect');
  body.setAttribute('x', '1');
  body.setAttribute('y', '5.5');
  body.setAttribute('width', '8');
  body.setAttribute('height', '6');
  body.setAttribute('rx', '1');
  body.setAttribute('fill', 'currentColor');
  svg.append(shackle, body);
  return svg;
}

/** 카드 위의 자물쇠 딱지. */
function lockMark(): HTMLElement {
  const m = el('span', { class: 'lockm', role: 'img', 'aria-label': '잠금' });
  m.append(lockIcon());
  return m;
}

export class SortieScreen {
  private readonly root: HTMLElement;
  private readonly army: Army;
  private readonly onStart: (r: SortieResult) => void;
  private readonly onChange: () => void;
  /** 장비 화면으로. `who`(HERO 또는 동료 id)를 주면 그 사람을 골라 둔 채 연다. */
  private readonly onArmory: (who?: string) => void;
  private readonly onStats: () => void;
  /** 전투 기록(`ui/records.ts`): 남긴 판의 수와 여는 길. */
  private readonly records: { count: number; open: () => void };
  private readonly onRealm: () => void;
  private readonly onReset: () => void;
  private readonly onRaid: (band?: number) => void;

  /** 분대별 배치(uid)와 분대별 동료 id. `meta/plan.ts`의 규칙이 이 둘을 고친다. */
  private squads: number[][] = [];
  private companions: (string | null)[] = [];
  /** 부관 — 장수 곁(내 부대)에서 싸우는 동료(`Plan.escort`). 부하 부대 자리는 차지하지 않는다(출정 때 `startRun`이 싣는다). */
  private escort: string | null = null;
  /** 두 칸. 처음에는 위(왼쪽)에 내 부대, 아래(오른쪽)에 병영. */
  private readonly slots: [Slot, Slot] = [
    { place: 0, filter: 'all', sort: 'roster' },
    { place: 'barracks', filter: 'all', sort: 'roster' },
  ];
  /** 한 번 보여 줄 알림(부대가 찼다 등). 다음 render에서 비운다. */
  private notice: string | null = null;
  /** 옮기지 못한 카드. 다시 그린 뒤 한 번 흔든다. */
  private shakeKey: string | null = null;
  /** 방금 옮긴 카드. 다시 그린 뒤 한 번 반짝인다. */
  private arrivedKey: string | null = null;
  /**
   * 다시 그린 뒤 포커스를 줄 카드. 카드를 옮기면 맞은편 칸에서 다른 열쇠(`c:칸:…`)로 다시 태어나므로,
   * 누른 단추를 같은 열쇠로 찾는 `anchorKey`로는 포커스가 사라졌다 — 키보드로 연달아 편성할 수
   * 없었다(리뷰가 짚었다). 상세 창에서 진급·옮기기를 눌렀을 때도 그 병사의 카드로 돌아온다.
   */
  private focusKey: string | null = null;
  /** 마지막으로 누른 단추의 열쇠(`data-key`). 다시 그린 뒤 그 단추를 같은 높이에 둔다. */
  private anchorKey: string | null = null;
  /**
   * 누른 단추가 `data-hold`를 달았으면 스크롤을 누르기 전 값 그대로 둔다(단추를 따라가지 않는다).
   * 카드와 진급이 그렇다: 누른 카드는 맞은편 칸으로 가 버리므로 따라갈 자리가 없다. 진급도 처음에는
   * 진급한 병사의 줄을 따라가 화면이 튀었다 — 플레이한 사람이 "스크롤 위치가 안 변하면 좋겠다"고 했다.
   * 포인터로 누를 때만이다: 키보드로 누르면 포커스가 옮겨 간 곳이 화면 밖에 남을 수 있어(리뷰가 짚었다)
   * 그곳을 따라간다.
   */
  private anchorHold = false;
  /**
   * 머리줄 밑에 얹는 알림(철수해 받은 것, `ui/homecoming.ts`). 화면은 누를 때마다 통째로 다시 그리므로(`render`) 밖에서
   * 한 번 끼워 넣은 것은 첫 누름에 사라진다 — 여기 쥐고 그릴 때마다 다시 놓는다. 닫으면 비운다.
   */
  homeNote: HTMLElement | null = null;

  constructor(
    root: HTMLElement,
    army: Army,
    onStart: (r: SortieResult) => void,
    onChange: () => void,
    onArmory: (who?: string) => void,
    onStats: () => void,
    onReset: () => void,
    onRaid: (band?: number) => void,
    onRealm: () => void,
    records: { count: number; open: () => void },
  ) {
    this.root = root;
    this.army = army;
    this.onStart = onStart;
    this.onChange = onChange;
    this.onArmory = onArmory;
    this.onStats = onStats;
    this.onRealm = onRealm;
    this.onReset = onReset;
    this.onRaid = onRaid;
    this.records = records;
    // 폰 여백 같은 출정 화면만의 배치를 이 클래스 아래로 가둔다. 공용 틀(.screen-scroll)을
    // 건드리면 장비·결과 화면까지 바뀌었다(리뷰가 짚었다).
    root.classList.add('sortie-screen');
    // 누른 단추를 기억한다. root는 다시 그려도 그대로라 한 번만 건다.
    //
    // click을 **잡기 단계**에서 본다 — 단추의 처리기(그 안에서 render가 돈다)보다 먼저다.
    // 처음에는 pointerdown만 봐서 키보드(Enter/Space)로 누르면 기억되지 않았고, 앞서 손가락으로
    // 누른 열쇠가 남아 엉뚱한 단추를 기준으로 스크롤을 맞췄다(리뷰가 짚었다).
    root.addEventListener(
      'click',
      (e) => {
        const hit = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-key]');
        this.anchorKey = hit?.dataset.key ?? null;
        // 키보드(Enter/Space)가 만든 click은 detail이 0이다.
        this.anchorHold = hit?.dataset.hold !== undefined && e.detail > 0;
      },
      true,
    );
    for (let i = 0; i < MAX_SQUADS; i++) {
      this.squads.push([]);
      this.companions.push(null);
    }
    // **들어오면 이미 편성돼 있다.** 가장 흔한 흐름("알아서 채우고 나간다")은 출정 한 번이면 된다.
    // 플레이한 사람이 "귀찮은 플레이어를 위해 자동 편성과 출정을 가장 잘 보이는 곳에" 달라고 했다.
    // 짜 둔 편성이 있으면 그대로 연다(`restorePlan`). 처음(저장 없음)만 자동 편성이다.
    const saved = restorePlan(army);
    if (saved) this.apply(saved);
    else this.fill();
    this.remember();
    // 둘째 칸은 부하 장수가 있으면 첫째 부하 부대, 없으면 병영(플레이한 사람이 정했다). 동료가 있는데
    // 병영을 띄우면 부하 부대를 보려고 매번 탭을 눌러야 했다.
    const firstMate = this.companions.findIndex((c, sq) => sq > 0 && c !== null);
    if (firstMate > 0) this.slots[1].place = firstMate;
  }

  /** 내 부대(장수 직할대)의 정원. 장수의 관등이 오를 때마다 늘어난다(`squadSize`). */
  private get size(): number {
    return squadSize(this.army);
  }

  /**
   * 분대 `sq`의 정원. 내 부대는 장수의 관등, 부하 부대는 맡은 동료의 관등이다(`capOf`, `mateSquadSize`). 예전에는 모든
   * 분대가 장수의 정원 하나를 썼다(Todo8).
   */
  private sizeOf(sq: number): number {
    return capOf(capacityOf(this.army), this.plan, sq);
  }

  /** 지금 편성을 계정에 적어 둔다. 저장은 `onChange`(persist)가 한다. */
  private remember(): void {
    this.army.formation = { squads: this.squads.map((s) => [...s]), companions: [...this.companions], escort: this.escort };
  }

  /** 편성이나 계정이 바뀌었다: 편성을 적어 두고 저장한다. */
  private changed(): void {
    this.remember();
    this.onChange();
  }

  private get plan(): Plan {
    return { squads: this.squads, companions: this.companions, escort: this.escort };
  }

  /**
   * 부하 부대는 **동료를 맡겨야 열린다.** 0번(내 부대)은 늘 열려 있다. 부관(장수 곁의 동료)은 부대 자리를 차지하지 않는다.
   *
   * 예전에는 동료가 없어도 네 부대에 병사를 나눴다. 전장에서는 "지휘관 없음" 부대 셋이 떴고,
   * 그 병사들은 결국 장수를 따라다녀 내 부대와 다를 것이 없었다.
   */
  private isOpen(sq: number): boolean {
    return sq === 0 || this.companions[sq] !== null;
  }

  /**
   * 탭으로 고를 수 있는 분대. 나설 수 있는 동료 수만큼 부하 부대가 열린다(`openSquads`와 같은 규칙). 짜 둔
   * 편성에서 동료가 뒷번호에 앉아 있으면(앞번 동료가 요양 중) 그 분대도 열려 있다(`restorePlan`).
   */
  private selectable(sq: number): boolean {
    return sq === 0 || sq <= availableCompanions(this.army).length || this.companions[sq] !== null;
  }

  private deployedCount(): number {
    return this.squads.reduce((n, s) => n + s.length, 0);
  }

  private power(): number {
    return selectionPower(this.army, this.squads.flat());
  }

  private riskCost(): number {
    let n = 0;
    for (const list of this.squads) {
      for (const uid of list) {
        const s = soldierById(this.army, uid);
        if (s) n += rebuildCost(TROOP_BY_ID.get(s.troop)!);
      }
    }
    return n;
  }

  /** 열린 분대를 정원까지 알아서 채운다. 규칙은 `autoPlan`(meta/plan.ts)에 있다. */
  private autoFill(): void {
    // 잠근 병사는 지금 자리에 둔다(`autoPlan`의 둘째 인자). 몇 명을 지켰는지 알린다 — 말이 없으면 "자동 편성이 왜 저
    // 병사를 안 옮기지"가 된다.
    // 요양·수비로 나설 수 없는 잠근 동료는 세지 않는다 — 자리에 둔 것이 아니다(처음에는 함께 세어 숫자가 부풀었다, QA).
    const ready = new Set(availableCompanions(this.army).map((c) => c.id));
    const mates = this.army.companions.filter((c) => c.locked === true && ready.has(c.id));
    const kept = this.army.soldiers.filter((s) => s.locked === true).length + mates.length;
    // 병영에서 잠근 동료는 부대를 맡지 않아 부대 하나가 닫힌다. 까닭을 적지 않으면 "왜 한 부대가 비지"가 된다.
    const idle = mates.filter((c) => this.escort !== c.id && !this.companions.includes(c.id)).map((c) => COMPANIONS.find((d) => d.id === c.id)?.name ?? c.id);
    this.apply(autoPlan(this.army, this.plan));
    if (kept > 0) {
      this.notice =
        `자동 편성했다. 잠근 ${kept}명은 있던 자리에 두었다.` +
        (idle.length > 0 ? ` 병영에서 잠근 ${idle.join('·')}${josa(idle[idle.length - 1], '은', '는')} 부대를 맡지 않는다.` : '');
    }
    this.changed();
    this.render();
  }

  /** 처음 여는 화면의 편성. 지킬 자리가 아직 없어 잠금을 보지 않는다. */
  private fill(): void {
    this.apply(autoPlan(this.army));
  }

  /** 편성을 통째로 갈아 끼운다. 이전 편성에 덧붙이지 않는다 — 덧붙이면 같은 병사가 두 번 든다. */
  private apply(plan: Plan): void {
    this.squads = plan.squads.map((s) => [...s]);
    this.companions = [...plan.companions];
    this.escort = plan.escort ?? null;
    this.fixSlots();
  }

  /**
   * 띄우던 부하 부대가 닫혔으면(동료가 요양에 들어갔다, 뒷번 동료를 옮겨 그 자리가 잠겼다) 그 칸은 내 부대·병영으로
   * 돌린다. 편성을 갈아 끼울 때와 옮긴 뒤에 부른다 — 옮긴 뒤에 안 부르면 잠긴 분대의 빈 머리가 칸에 남았다(리뷰가 짚었다).
   */
  private fixSlots(): void {
    for (const slot of this.slots) {
      if (slot.place !== 'barracks' && !this.selectable(slot.place)) slot.place = 0;
    }
    if (this.slots[0].place === this.slots[1].place) this.slots[1].place = 'barracks';
  }

  private clearAll(): void {
    // 잠근 사람은 남긴다: 잠근 병사와, 그 병사가 앉은 부대의 동료(동료 없는 부대는 병사를 둘 수 없다), 잠근 동료.
    const lockedMate = (id: string | null): boolean => id !== null && this.army.companions.find((c) => c.id === id)?.locked === true;
    let kept = 0;
    for (let i = 0; i < MAX_SQUADS; i++) {
      this.squads[i] = this.squads[i].filter((uid) => soldierById(this.army, uid)?.locked === true);
      kept += this.squads[i].length;
      if (lockedMate(this.companions[i])) kept++;
      else if (this.squads[i].length === 0) this.companions[i] = null;
    }
    // 부관도 비운다. 남겨 두면 편성을 비웠는데도 부관만 남아 다른 동료가 부관이 되지 못했다(리뷰가 짚었다). 잠근 부관은 남는다.
    if (lockedMate(this.escort)) kept++;
    else this.escort = null;
    this.notice = kept > 0 ? `편성을 비웠다. 잠근 ${kept}명은 남겼다.` : null;
    this.changed();
    this.render();
  }

  /**
   * 병영을 통째로 싣고 곧장 출정한다. 시험 모드 전용이다. 들어올 때 채운 자동 편성을
   * 버리고 고르게 나누는 규칙(`evenPlan`)으로 다시 싣는다 — 이전 편성 위에 쌓으면
   * 같은 병사가 두 번 들어갔다.
   */
  deployAllAndStart(): void {
    this.apply(evenPlan(this.army));
    this.start();
  }

  /** 고른 출발 깊이. 열린 것 가운데 마지막으로 고른 것, 없으면 1. */
  private startDepth(): number {
    const open = startDepths(this.army);
    const want = this.army.startDepth ?? 1;
    return open.includes(want) ? want : 1;
  }

  private start(): void {
    if (this.deployedCount() === 0) return;
    const wage = wageFor(this.army, this.squads.flat());
    if (wage > this.army.gold) {
      // 모자란 것은 급료 전액이 아니라 그 차이다(리뷰가 짚었다).
      this.notice = `급료 ${wage}냥에 ${wage - this.army.gold}냥이 모자라다(가진 돈 ${this.army.gold}냥). 데려갈 병사를 줄이거나, 단신 토벌로 돈을 벌자.`;
      this.render();
      return;
    }
    this.remember();
    this.onStart({
      selection: { squads: this.squads.map((s) => [...s]), companions: [...this.companions], escort: this.escort },
      // 출정 화면이 미리 보인 전장 특성과 같은 시드(`nextRunSeed`)로 떠난다.
      seed: nextRunSeed(this.army),
      startDepth: this.startDepth(),
    });
  }

  // ------------------------------------------------------------------
  // 옮기기

  private placeName(p: Place): string {
    if (p === 'barracks') return '병영';
    if (p === 0) return '내 부대';
    return squadName(p);
  }

  private companionAt(sq: number): CompanionDef | undefined {
    const id = this.companions[sq];
    return id ? COMPANIONS.find((c) => c.id === id) : undefined;
  }

  private otherPlace(slot: number): Place {
    return this.slots[1 - slot].place;
  }

  /** 옮긴 결과를 알림으로. 못 옮겼으면 그 카드를 흔든다. `to`는 옮겨 간 곳. */
  private report(r: MoveResult, key: string, done: string, to: Place): void {
    const [, slot, who] = key.split(':');
    if (r.ok) {
      this.fixSlots();
      // 옮긴 카드는 그곳을 띄운 칸에 선다. 두 칸 어디에도 없으면(상세에서 안 보이는 부대로 보냈다) 원래 칸의
      // 탭이 포커스를 받는다(`render`의 대체 경로).
      const at = this.slots.findIndex((x) => x.place === to);
      const moved = at >= 0 ? `c:${at}:${who}` : `c:${slot}:${who}`;
      this.arrivedKey = at >= 0 ? moved : null;
      this.focusKey = moved;
      this.notice = r.back > 0 ? `${done} 내 부대 정원이 차서 ${r.back}명은 병영으로 돌아갔다.` : done;
      this.changed();
    } else {
      this.shakeKey = key;
      this.focusKey = key;
      this.notice = {
        full: `${this.placeName(to)}가 찼다(${typeof to === 'number' ? this.sizeOf(to) : this.size}명). 한 명을 빼고 넣자.`,
        noCommander: '지휘관이 없는 부대다. 병영의 부하장수 카드를 먼저 앉히자.',
        leaderSquad: '내 부대는 장수가 이끈다. 부하장수는 부하 부대를 맡는다.',
        occupied: '이미 지휘관이 있다. 앉은 부하장수 카드를 먼저 병영으로 보내자.',
        escortTaken: '부관은 한 명뿐이다. 지금 부관을 먼저 부하 부대나 병영으로 보내자.',
        noSeat: '부관이 출정할 때 쓸 빈 부대 자리가 하나는 있어야 한다. 부대 하나의 부하장수를 병영으로 보내자.',
      }[r.why];
    }
    this.render();
  }

  /** `near`가 있으면(다른 부대의 병사 카드 위에 놓았다) 옮긴 뒤 그 카드 자리에 선다 — 끝에 붙이지 않고. */
  private moveSoldierTo(s: Soldier, slot: number, to: Place, near?: number): void {
    const key = `c:${slot}:s${s.uid}`;
    const r = moveSoldier(this.plan, s.uid, to, capacityOf(this.army));
    if (r.ok && near !== undefined) reorderSoldier(this.plan, s.uid, near);
    this.report(r, key, `${s.name}${josa(s.name, '을', '를')} ${this.placeName(to)}(으)로 옮겼다.`, to);
  }

  private moveCompanionTo(c: CompanionDef, slot: number, to: Place): void {
    const key = `c:${slot}:h${c.id}`;
    const done =
      to === 'barracks'
        ? `${c.name}${josa(c.name, '이', '가')} 병영으로 물러났다.`
        : to === 0
          ? `${c.name}${josa(c.name, '이', '가')} 부관이 되었다. 지휘 보정이 내 부대에 더해진다.`
          : `${c.name}${josa(c.name, '이', '가')} 제${to + 1}부대를 맡았다.`;
    // `plan`은 부를 때마다 새로 묶는 틀이라, 옮긴 뒤 곁의 동료(`escort`)를 되읽는다.
    const p = this.plan;
    const r = moveCompanion(p, c.id, to, capacityOf(this.army));
    this.escort = p.escort ?? null;
    this.report(r, key, done, to);
  }

  /**
   * 끌어서 놓을 곳: 탭 위면 그 탭의 부대, 칸 위면 그 칸이 띄운 곳. 아무 데도 아니면 null.
   * 잠긴 탭은 `locked`로 알린다 — 왜 못 가는지 적으려고.
   */
  private dropTarget(
    x: number,
    y: number,
  ): { place: Place; locked: boolean; node: HTMLElement; card?: { uid: number; node: HTMLElement } } | null {
    const hit = document.elementFromPoint(x, y);
    if (!hit || !this.root.contains(hit)) return null;
    // 병사 카드 위: 같은 분대면 순서 바꾸기에 쓴다(`c:칸:s병사uid`).
    const cardNode = hit.closest<HTMLElement>('button.ucard');
    const ck = cardNode?.dataset.key?.split(':');
    const card = cardNode && ck && ck[2]?.startsWith('s') ? { uid: Number(ck[2].slice(1)), node: cardNode } : undefined;
    const tab = hit.closest<HTMLElement>('button.tab');
    if (tab?.dataset.key) {
      const raw = tab.dataset.key.split(':')[2];
      const place: Place = raw === 'barracks' ? 'barracks' : Number(raw);
      return { place, locked: tab.classList.contains('locked'), node: tab };
    }
    const box = hit.closest<HTMLElement>('.holder');
    if (box) {
      const slot = Number(box.dataset.slot);
      return { place: this.slots[slot].place, locked: false, node: box, card };
    }
    return null;
  }

  /**
   * 그곳으로 옮길 수 있나(끌 때 놓일 곳을 칠하는 데 쓴다). `moveSoldier`·`moveCompanion`의 거절과 같은 답이어야
   * 한다 — 처음에는 따로 셈해, 동료를 내 부대에 끌면 칠해지고 놓으면 거절됐다(리뷰가 짚었다). 정원이 찬 부대는 여기서 빼지
   * 않는다: 놓으면 "찼다"고 알리는 편이 왜 안 되는지 보인다.
   * - 병사: 병영, 내 부대, 지휘관(동료)이 앉은 부하 부대.
   * - 동료: 병영, 비어 있고 열 수 있는 부하 부대, 부관(내 부대 — 부관이 비어 있을 때). 부관이 있으면 병영에서 새로
   *   앉을 때 빈 부대 자리가 하나는 남아야 한다(`noSeat`, 부관이 출정할 때 쓴다).
   */
  private canGo(companion: boolean, to: Place, from: Place): boolean {
    if (to === 'barracks') return true;
    if (companion) {
      const seat = freeSeats(this.plan);
      if (to === 0) return this.escort === null && (from !== 'barracks' || seat > 0);
      if (!this.selectable(to) || this.companions[to] !== null) return false;
      return from !== 'barracks' || this.escort === null || seat > 1;
    }
    return this.isOpen(to);
  }

  /**
   * 끄는 동안 놓일 곳을 칠하고, 놓으면 그리로 옮긴다. 제자리에 놓으면 아무 일도 없다. 갈 수 없는 곳은 칠하지
   * 않지만, 놓으면 옮기기를 시도해 까닭을 알린다(`report`). `key`는 끄는 카드 — 못 가면 흔들고 포커스를 둔다.
   */
  private cardDrag(
    from: Place,
    companion: boolean,
    key: string,
    go: (to: Place, near?: number) => void,
    reorder?: (target: number) => void,
  ): CardDrag {
    // 같은 분대의 다른 병사 카드 위에 놓으면 순서를 바꾼다. 병영은 거르기·줄 세우기가 순서를 정하므로 빼고.
    const self = key.split(':')[2];
    const swapWith = (t: ReturnType<typeof this.dropTarget>): { uid: number; node: HTMLElement } | undefined =>
      reorder && from !== 'barracks' && t && t.place === from && t.card && `s${t.card.uid}` !== self ? t.card : undefined;
    let lit: HTMLElement | null = null;
    const light = (n: HTMLElement | null): void => {
      if (lit === n) return;
      lit?.classList.remove('drop-on');
      lit = n;
      lit?.classList.add('drop-on');
    };
    return {
      move: (x, y) => {
        const t = this.dropTarget(x, y);
        const swap = swapWith(t);
        light(swap ? swap.node : t && t.place !== from && !t.locked && this.canGo(companion, t.place, from) ? t.node : null);
      },
      drop: (x, y) => {
        const t = this.dropTarget(x, y);
        const swap = swapWith(t);
        if (swap && reorder) {
          this.anchorHold = true;
          reorder(swap.uid);
          return;
        }
        if (!t || t.place === from) return;
        if (t.locked) {
          this.notice = lockedWhy(t.place as number, metCompanions(this.army).length);
          this.shakeKey = key;
          this.focusKey = key;
          this.anchorHold = true;
          this.render();
          return;
        }
        // 손가락이면 화면을 움직이지 않는다(`render`).
        this.anchorHold = true;
        go(t.place, t.card?.uid);
      },
      end: () => light(null),
    };
  }

  // ------------------------------------------------------------------

  /**
   * 화면 전체가 한 번만 세로로 스크롤된다. 출정 막대는 늘 화면 맨 아래, 길잡이 줄은 늘 맨 위에 붙는다.
   * 카드 줄과 탭 줄은 각자 옆으로 민다.
   */
  render(): void {
    // **누른 것이 제자리에 있게 한다.** 화면을 통째로 지웠다 다시 그리면, 지우는 순간 내용
    // 높이가 0이 되어 브라우저가 스크롤을 맨 위로 당긴다. 그래서 세로 스크롤을 되돌리고, 방금
    // 누른 단추가 전과 같은 높이에 오도록 한 번 더 민다(`data-hold`면 밀지 않는다).
    // 칸마다의 가로 스크롤(탭 줄·카드 줄)도 같은 것을 띄우고 있으면 되돌린다 — 카드를 옮길 때마다
    // 카드 줄이 처음으로 돌아가면 줄 가운데 병사를 연달아 옮길 수 없다.
    const scroller = this.root.parentElement;
    const prevScroll = scroller?.scrollTop ?? 0;
    const key = this.anchorKey;
    const hold = this.anchorHold;
    this.anchorKey = null;
    this.anchorHold = false;
    const before = key ? this.keyed(key)?.getBoundingClientRect().top : undefined;
    const hadFocus = key !== null && document.activeElement === this.keyed(key);
    // 포커스가 이 화면(상세 창 포함) 안에 있었는가. 없었으면 포커스를 옮기지 않는다 — 남의 것을 빼앗는다.
    // 상세 창을 닫고 옮기면 포커스는 body에 있다. 그것도 이 화면 안으로 친다.
    const act = document.activeElement;
    const wasInside = act === null || act === document.body || (this.root.parentElement ?? this.root).contains(act);
    const focusKey = this.focusKey;
    this.focusKey = null;
    const sideScroll = this.slots.map((slot, i) => {
      const box = this.root.querySelector<HTMLElement>(`.holder[data-slot="${i}"]`);
      const same = box?.dataset.place === String(slot.place);
      return {
        tabs: box?.querySelector('.tabs')?.scrollLeft ?? 0,
        cards: same ? (box?.querySelector('.cards')?.scrollLeft ?? 0) : 0,
      };
    });
    clear(this.root);
    const notice = this.notice;
    this.notice = null;
    this.root.append(
      this.nav(),
      // 돌아온 소식(`homeNote`)은 머리줄 바로 밑에. 닫기 전까지 다시 그려도 남는다.
      ...(this.homeNote ? [this.homeNote] : []),
      this.ledger(),
      el('div', { class: 'slots' }, this.holder(0), this.holder(1)),
      this.supplyShop(),
      // 영토: 이번 차례의 소식과 지도로 가는 단추(`realmSection`). 머리줄에 있던 영토 단추를 대신한다.
      realmSection(this.army, this.onRealm),
      this.others(),
      this.actionBar(notice),
    );
    this.slots.forEach((_, i) => {
      const box = this.root.querySelector<HTMLElement>(`.holder[data-slot="${i}"]`);
      const tabs = box?.querySelector<HTMLElement>('.tabs');
      const cards = box?.querySelector<HTMLElement>('.cards');
      if (tabs) {
        tabs.scrollLeft = sideScroll[i].tabs;
        showSelectedTab(tabs);
      }
      if (cards) cards.scrollLeft = sideScroll[i].cards;
      for (const f of box?.querySelectorAll<HTMLElement>('.hint') ?? []) refreshHint(f);
    });
    for (const [k, cls] of [[this.shakeKey, 'shake'], [this.arrivedKey, 'arrive']] as const) {
      if (k) this.keyed(k)?.classList.add(cls);
    }
    this.shakeKey = null;
    this.arrivedKey = null;
    // 열린 상세 창은 두고 내용만 새 상태로(진급·모집 뒤 숫자가 바뀐다).
    this.fillDetail();
    if (!scroller) return;
    scroller.scrollTop = prevScroll;
    const next = key ? this.keyed(key) : null;
    const after = next?.getBoundingClientRect().top;
    if (!hold && before !== undefined && after !== undefined) scroller.scrollTop += after - before;
    // 누른 단추는 지워지며 포커스를 잃는다. 폰 브라우저는 그때 스크롤을 옮기기도 해서,
    // 새로 그린 같은 단추에 포커스를 스크롤 없이 돌려준다.
    // 옮겨 간 카드가 거르기에 걸려 안 보이면(병영을 '창'으로 거른 채 기병을 보냈다) 그 칸의 고른 탭으로 —
    // 포커스가 본문으로 떨어지면 키보드로 이어 갈 수 없다(리뷰가 짚었다).
    const target = focusKey
      ? (this.keyed(focusKey) ??
        this.root.querySelector<HTMLElement>(`.holder[data-slot="${focusKey.split(':')[1]}"] button.tab[aria-pressed="true"]`))
      : null;
    if (target && wasInside) {
      // 키보드로 옮겼으면 옮겨 간 카드가 보이게 끌어온다. 손가락이면 화면을 움직이지 않는다(`data-hold`).
      target.focus({ preventScroll: hold });
    } else if (hadFocus && next) {
      next.focus({ preventScroll: true });
    }
  }

  private keyed(key: string): HTMLElement | null {
    return this.root.querySelector<HTMLElement>(`[data-key="${CSS.escape(key)}"]`);
  }

  /**
   * 늘 보이는 길잡이 줄: 제목, 군자금, 능력치·장비·밝기. 스크롤을 어디까지 내려도 맨 위에 붙어 있다.
   * 예전에는 머리에만 있어 병영을 내려가다 장비를 보려면 맨 위까지 다시 올라가야 했다(플레이한 사람이 짚었다).
   */
  private nav(): HTMLElement {
    const screen = this.root.parentElement;
    // 지금 고른 밝기를 적는다. 처음에는 둘뿐이라 누르면 될 쪽("어둡게")을 적었는데, 셋(자동)이 되자 다음 것을
    // 적으면 지금이 무엇인지 알 수 없었다.
    const theme = button(screen ? THEME_LABEL[currentChoice(screen)] : '밝게', () => {
      if (!screen) return;
      cycleTheme(screen);
      this.render();
    }, 'mini');
    theme.title = '메뉴 화면의 밝기: 누를 때마다 밝게 → 어둡게 → 자동(폰·PC 설정을 따른다). 전장은 그대로다.';
    const points = freeSkillPoints(this.army);
    const enhance = canEnhanceWorn(this.army);
    return el(
      'header',
      { class: 'sortie-nav' },
      el('b', { class: 'nav-title' }, '출정 준비'),
      el('span', { class: 'nav-gold' }, `${this.army.gold.toLocaleString()}냥`),
      el('span', { class: 'nav-gap' }),
      // 할 일이 있는 화면에는 표를 단다: 쓸 기술 점수(능력치), 강화할 수 있는 물건(장비). 둘 다 그 화면 안에 있어
      // 들어가 보지 않으면 있는 줄 모른다(Todo10 뒤의 안내).
      // 표는 짧게(숫자·★) — 폰 너비에서 "기술 2"·"강화"로 적었더니 머리줄이 넘쳐 글자가 한 자씩 접혔다. 뜻은 읽어 주는
      // 이름(`aria-label`)과 제목에 적는다.
      navButton('능력치', this.onStats, points > 0 ? String(points) : '', points > 0 ? `쓸 기술 점수 ${points}` : ''),
      navButton('장비', () => this.onArmory(), enhance ? '★' : '', enhance ? '입은 장비 가운데 강화할 수 있는 것이 있다' : ''),
      theme,
    );
  }

  /**
   * 도적 무리를 고른다(`RAID_BANDS`). 무리마다 한 줄: 이름, 머릿수와 현상금, 나서는 단추. 닫힌 무리는 까닭을 적는다
   * (관등이 모자라다 / 흩어졌다 — 출정에서 한 판 이기면 다시 모인다). 아직 열 수 없는 무리는 바로 다음 것 하나만
   * 보인다 — 처음 하는 사람에게 여섯 줄을 다 보이면 길다.
   *
   * 플레이한 사람이 청했다: "혼자 나선다도 나중에는 돈 좀 많이 벌 수 있게 해줘. 장수가 크니까 적이 너무 약하고 돈도
   * 너무 적어." 가장 작은 무리는 예전의 그 판 그대로다(부대를 다 잃었을 때 일어서는 길).
   */
  private raidBands(): HTMLElement {
    const rank = RANKS.indexOf(rankFor(this.army.totalMerit));
    const rows = raidBandsOpen(this.army)
      .filter((c) => c.band <= rank + 1)
      .map((c) => {
        const def = RAID_BANDS[c.band];
        const men = def.roster.reduce((n, e) => n + e.count, 0);
        const go = withKey(button(c.open ? '나선다' : '닫힘', () => this.onRaid(c.band), 'mini'), `raid:${c.band}`);
        go.disabled = !c.open;
        go.setAttribute('aria-label', c.open ? `${def.name}에 혼자 나선다, 현상금 ${def.bounty.toLocaleString()}냥` : `${def.name} — ${c.why}`);
        return el(
          'li',
          { class: c.open ? 'raid-band' : 'raid-band shut' },
          el(
            'span',
            { class: 'raid-txt' },
            el('b', {}, def.name),
            el('span', { class: 'dim' }, c.open ? `도적 ${men}명 · 현상금 ${def.bounty.toLocaleString()}냥` : c.why),
            c.open ? el('span', { class: 'raid-note' }, def.note) : false,
          ),
          go,
        );
      });
    return el('ul', { class: 'raid-bands' }, ...rows);
  }

  /** 원장: 보유 병력과 전과. 군자금은 길잡이 줄에 있다. 관등·장수 레벨은 내 부대 지휘관 줄과 장수 상세로 옮겼다. */
  private ledger(): HTMLElement {
    const a = this.army;
    return el(
      'div',
      { class: 'sortie-ledger' },
      el('span', { class: 'eyebrow' }, 'ILDANGBAEK 일당백'),
      el(
        'div',
        { class: 'ledger' },
        stat('보유 병력', `${a.soldiers.length}명`),
        // 용사는 하나라도 세웠거나 세울 수 있는 병사가 있을 때만 적는다 — 초반의 원장을 늘리지 않는다.
        ...(elitesOf(a).length > 0 || a.soldiers.some((x) => eliteBody(x.troop, soldierLevel(x))) ? [stat('용사', `${elitesOf(a).length}/${eliteCap(a)}`)] : []),
        stat('가장 깊이', a.deepest > 0 ? `깊이 ${a.deepest}` : '—'),
        stat('최고 전과', a.bestMerit.toLocaleString()),
        stat('잃은 병사', String(a.fallen)),
      ),
      // 지난 판 다시 보기. 길잡이 줄(능력치·장비)은 폰 너비에서 이미 꽉 차 있어 여기 둔다. 남긴 판이 없어도 보인다 —
      // 들어가면 무엇이 남는지 적혀 있다.
      // 소리 단추도 여기 둔다 — 길잡이 줄은 360 폭에서 이미 꽉 찼고, 이름표 줄의 오른쪽이 비어 있다.
      el('div', { class: 'ledger-tools' }, this.soundButton(), this.recordsButton()),
    );
  }

  /** 소리: 끔 → 작게 → 크게. 밝기 단추처럼 지금 고른 것을 적는다. 켜면 그 크기로 한 번 들려준다(`audio/sound.ts`). */
  private soundButton(): HTMLButtonElement {
    const b = button(LEVEL_LABEL[soundLevel()], () => {
      cycleSound();
      this.render();
    }, soundChosen() ? 'mini sound-toggle' : 'mini sound-toggle fresh');
    // 이 단추는 단추 소리 대신 제 소리를 낸다(`initSound`가 이 표시를 보고 가른다).
    b.setAttribute('data-sound-toggle', '');
    // 누르면 화면을 다시 그린다 — 열쇠가 있어야 새로 그린 단추가 초점을 돌려받는다. 없을 때는 키보드로 한 번 누르면
    // 초점이 본문으로 빠져, 이어서 눌러 단계를 넘길 수 없었다(리뷰).
    b.setAttribute('data-key', 'tool:sound');
    b.title = '소리: 누를 때마다 끔 → 작게 → 크게. 처음에는 꺼져 있다.';
    return b;
  }

  private recordsButton(): HTMLButtonElement {
    const n = this.records.count;
    const b = button(n > 0 ? `▶ 전투 기록 ${n}판` : '▶ 전투 기록', this.records.open, 'mini records-open');
    b.title = '지난 판을 다시 본다 — 최근 다섯 판과 전과가 가장 높은 한 판이 남는다';
    return b;
  }

  // ------------------------------------------------------------------
  // 칸

  private holder(i: number): HTMLElement {
    const slot = this.slots[i];
    const p = slot.place;
    const color = p === 'barracks' ? 'var(--sq-bar)' : `var(--sq${p})`;
    return el(
      'section',
      {
        class: 'holder',
        'data-slot': String(i),
        'data-place': String(p),
        style: `--c:${color}`,
        'aria-label': `${i === 0 ? '첫째' : '둘째'} 칸 · ${this.placeName(p)}`,
      },
      hinted(this.tabs(i), 'tabs-wrap', '탭'),
      el(
        'div',
        { class: 'hbody' },
        ...(p === 'barracks' ? this.barracksBody(i) : this.squadBody(i, p)),
      ),
    );
  }

  /**
   * 칸에 띄울 것을 고르는 탭: 내 부대 · 부하 부대 셋 · 병영. 한 줄이고 넘치면 옆으로 민다.
   * 맞은편 칸에 떠 있는 것을 고르면 두 칸이 서로 바뀐다.
   *
   * 열 수 없는 부하 부대도 탭은 보인다(잠김). 무엇을 하면 열리는지 적어 둔다 — 새 계정은 부대가
   * 늘어난다는 것조차 알 수 없었다.
   *
   * **잠긴 부대는 다음에 열릴 하나만 보인다.** 처음에는 셋을 다 보였다 — 새 계정의 탭 줄이 "제2부대 잠김 · 제3부대 잠김 ·
   * 제4부대 잠김"으로 차서, 쓸 수 있는 것(내 부대·병영)보다 못 쓰는 것이 많았다. 플레이한 사람이 정했다(2026-10-08):
   * "2부대 잠김만 보여주고, 3, 4는 숨기자. 2부대를 얻으면 3부대 잠김을 보여주고."
   */
  private tabs(i: number): HTMLElement {
    const here = this.slots[i].place;
    const there = this.otherPlace(i);
    const nav = el('nav', { class: 'tabs', 'aria-label': `${i === 0 ? '첫째' : '둘째'} 칸에 띄울 것` });
    const places: Place[] = [...Array.from({ length: MAX_SQUADS }, (_, sq) => sq), 'barracks'];
    const met = metCompanions(this.army).length;
    let lockedShown = false;
    for (const p of places) {
      const locked = p !== 'barracks' && !this.selectable(p);
      // 잠긴 부대는 번호가 가장 앞선 하나만(위 주석).
      if (locked) {
        if (lockedShown) continue;
        lockedShown = true;
      }
      const color = p === 'barracks' ? 'var(--sq-bar)' : `var(--sq${p})`;
      let label: string;
      let count = '';
      if (p === 'barracks') {
        label = '병영';
        count = String(this.barracksSoldiers().length + this.barracksCompanions().length);
      } else if (locked) {
        label = `${squadName(p as number)} 잠김`;
      } else {
        // 부대 이름은 번호다(제2부대). 맡은 동료 이름으로 부르면 동료를 옮길 때 부대가 따라 옮는 것처럼 보였다
        // (플레이한 사람: "부하 부대의 장수를 고정하지 말아줘 … 그냥 제2부대 이런식으로").
        label = squadName(p);
        count = `${this.squads[p].length}/${this.sizeOf(p)}`;
      }
      const t = withKey(
        button('', () => {
          if (locked) {
            this.notice = lockedWhy(p as number, met);
            this.render();
            return;
          }
          if (p === there) this.slots[1 - i].place = here;
          this.slots[i].place = p;
          this.render();
        }, `tab${locked ? ' locked' : ''}`),
        `tab:${i}:${p}`,
      );
      t.style.setProperty('--t', color);
      t.setAttribute('aria-pressed', String(p === here));
      if (locked) {
        t.setAttribute('aria-disabled', 'true');
        t.title = lockedWhy(p as number, met);
      }
      t.append(el('i', { class: 'pip', 'aria-hidden': 'true' }), label);
      if (count) t.append(el('span', { class: 'n' }, count));
      // 그 칸에 진급할 수 있는 병사가 있으면 탭에도 빨간 점 — 탭을 열어 보지 않아도 안다(플레이한 사람이 청했다).
      if (!locked && this.promotableAt(p)) {
        t.append(el('span', { class: 'tab-dot', role: 'img', 'aria-label': '진급할 수 있는 병사가 있다' }));
      }
      if (p === there) {
        t.append(
          el(
            'span',
            { class: 'there' },
            el('span', { class: 'narrow-only' }, i === 0 ? '아래' : '위'),
            el('span', { class: 'wide-only' }, i === 0 ? '오른쪽' : '왼쪽'),
          ),
        );
      }
      nav.append(t);
    }
    return nav;
  }

  /**
   * 분대 병사를 **분대 배열 순서 그대로**. 예전에는 병종 순서(`rosterOrder`)로 늘 다시 세웠는데, 플레이한 사람이
   * 자동 정렬은 끄고 순서는 끌어서 직접 바꾸자고 했다(`reorderSoldier`). 이 순서가 편성과 함께 저장된다.
   */
  private squadSoldiers(sq: number): Soldier[] {
    return this.squads[sq].map((uid) => soldierById(this.army, uid)).filter((s): s is Soldier => !!s);
  }

  /**
   * 내 부대의 선호 병종(`Army.heroBranch`). 자동 편성이 그 병종을 내 부대에 먼저 채운다 — 부하 부대는 그 사람이 잘
   * 부리는 병종을 먼저 받는다(`autoPlan`). 고르는 것만으로 편성이 바뀌지는 않는다: 자동 편성을 눌러야 옮긴다.
   */
  private heroBranchPicker(i: number): HTMLElement {
    const sel = withKey(el('select', { class: 'pref-select', 'aria-label': '내 부대의 선호 병종', title: '자동 편성이 이 병종을 내 부대에 먼저 채운다' }), `pref:${i}`) as HTMLSelectElement;
    const now = this.army.heroBranch ?? '';
    const opt = (value: string, label: string): void => {
      const o = el('option', { value }, label);
      if (value === now) o.setAttribute('selected', 'selected');
      sel.append(o);
    };
    opt('', '선호 병종 없음');
    for (const [b, name] of [[Branch.Blade, '도검'], [Branch.Spear, '창'], [Branch.Bow, '궁수'], [Branch.Horse, '기병'], [Branch.Levy, '민병']] as const) opt(b, `선호 · ${name}`);
    sel.addEventListener('change', () => {
      if (sel.value === '') delete this.army.heroBranch;
      else this.army.heroBranch = sel.value as Branch;
      this.notice = sel.value === '' ? '내 부대의 선호 병종을 지웠다.' : `내 부대의 선호 병종: ${sel.selectedOptions[0].textContent?.replace('선호 · ', '')}. 자동 편성을 누르면 먼저 채운다.`;
      this.focusKey = `pref:${i}`;
      this.changed();
      this.render();
    });
    return sel;
  }

  private squadBody(i: number, sq: number): HTMLElement[] {
    const list = this.squads[sq];
    const soldiers = this.squadSoldiers(sq);
    const head = el(
      'div',
      { class: 'hhead' },
      el('h2', {}, this.placeName(sq)),
      // 병사 없이 동료만 앉은 분대는 동료가 혼자 나선다(단신 출전, `createBattle`). 빈 분대로 보이면 안 나가는 줄 안다.
      el(
        'span',
        { class: 'cnt' },
        `${list.length}/${this.sizeOf(sq)}명` + (sq > 0 && list.length === 0 && this.companionAt(sq) ? ' · 단신 출전' : ''),
      ),
      el('span', { class: 'pw' }, `전투력 ${selectionPower(this.army, list).toLocaleString()}`),
      sq === 0 && this.heroBranchPicker(i),
      list.length > 0 &&
        el(
          'span',
          { class: 'act' },
          this.batchButton(i, soldiers),
          ...this.branchButtons(i, soldiers),
          withKey(
            button('모두 병영으로', () => {
              // 잠근 병사는 남긴다(플레이한 사람이 정했다 — 잠금은 한꺼번에 옮기는 일을 모두 막는다).
              const stay = this.squads[sq].filter((uid) => soldierById(this.army, uid)?.locked === true);
              const sent = this.squads[sq].length - stay.length;
              this.squads[sq] = stay;
              this.notice =
                sent === 0
                  ? `${this.placeName(sq)}의 병사는 모두 잠겨 있다. 옮기려면 잠금을 풀거나 끌어서 옮긴다.`
                  : `${this.placeName(sq)}의 병사 ${sent}명을 병영으로 보냈다.${stay.length > 0 ? ` 잠근 ${stay.length}명은 남겼다.` : ''}`;
              // 단추가 사라지므로 키를 남겨 둔다 — 없으면 포커스가 body로 떨어진다. 같은 칸의 탭이 받는다.
              this.focusKey = `empty:${i}`;
              this.changed();
              this.render();
            }, 'mini'),
            `empty:${i}`,
          ),
        ),
    );

    const cards = el('div', { class: 'cards' });
    // 장수 곁의 동료는 병사 카드와 같은 줄 맨 앞에 선다. 처음에는 장수 줄 밑에 "곁" 줄을 따로 두었는데 세로로 너무
    // 길어졌다(플레이한 사람이 짚었다). 병사 정원은 차지하지 않는다 — 빈자리 수는 병사로만 센다.
    const esc = sq === 0 && this.escort ? COMPANIONS.find((x) => x.id === this.escort) : undefined;
    if (esc) cards.append(this.companionCard(esc, i));
    for (const s of soldiers) cards.append(this.soldierCard(s, i));
    // 빈자리도 보인다 — 정원이 몇인지 세지 않아도 안다. 닫힌 부대는 빈자리를 그리지 않는다.
    if (this.isOpen(sq)) for (let k = soldiers.length; k < this.sizeOf(sq); k++) cards.append(el('span', { class: 'slot-ph', 'aria-hidden': 'true' }));

    // 기병을 걷는 지휘관에게 맡기면 기병이 느려진다. 병사는 지휘관을 따라가고 지휘관이 걸으면
    // 그 걸음이 된다. 여기서 알려 주지 않으면 전장에 나가서야 안다.
    const cmdrKey = sq === 0 ? HERO : this.companions[sq];
    const walkingCavalry =
      cmdrKey !== null &&
      !isMounted(loadoutOf(this.army, cmdrKey)) &&
      soldiers.some((s) => TROOP_BY_ID.get(s.troop)?.branch === Branch.Horse);

    return [
      head,
      this.commanderRow(i, sq),
      hinted(cards, 'cards-wrap', '카드'),
      walkingCavalry &&
        el(
          'p',
          { class: 'hint warn' },
          `${sq === 0 ? '장수' : (this.companionAt(sq)?.name ?? '지휘관')}에게 말이 없다. 기병이 걷는 지휘관을 따라 느려진다 — 장비에서 말을 채워 주자.`,
        ),
    ].filter((n): n is HTMLElement => n instanceof HTMLElement);
  }

  /**
   * 지휘관 줄. 내 부대는 장수(관등·레벨·전과·지휘), 부하 부대는 맡은 동료와 휘하 보정.
   * 예전 "출정" 칸에 있던 관등·레벨·전과를 이리로 옮겼다 — 그 칸은 잘 보지 않았다(플레이한 사람이 짚었다).
   */
  private commanderRow(i: number, sq: number): HTMLElement {
    if (sq === 0) {
      // 제목 · 보정 칩 · 레벨 막대 · 관등 막대, 네 줄. 처음에는 지휘·관등·누적 전과·지휘 보정·부관 보정·분대 정원·
      // 다음 레벨과 관등까지를 일곱 줄 글로 적어 "글자가 많아 한눈에 안 들어온다"(플레이한 사람). 누적 전과는 장수
      // 상세에 있고, 지금 정원은 부대 머리(14/14명)에 있다.
      const a = this.army;
      const lv = heroLevelOf(a);
      const rank = rankFor(a.totalMerit);
      const { level: lp, rank: rp } = heroProgress(a);
      // 월급(`RANK_SALARY`): 출정을 철수로 마칠 때 받는다. 편장은 0이라 칩을 두지 않는다. "월급 N냥"만 적었더니 출정마다
      // 그냥 들어오는 돈처럼 읽혔다(QA) — 받는 때를 칩에 적는다.
      // 조건도 칩에 적는다: 제 관등의 온 몫은 그 출정에서 어느 깊이 이상을 이겨야 나온다(`salarySteps`의 첫 줄). 금액만
      // 적었더니 한 판 이기고 철수하면 늘 나오는 줄 알았다(플레이한 사람이 물었다). 얕은 곳의 몫과 없는 경우는 장수 상세에.
      const steps = salarySteps(a);
      const pay = steps.length > 0 ? `철수 월급 ${steps[0].pay.toLocaleString()}냥 · 깊이 ${steps[0].from}↑` : '';
      const chips = [commandShort(lv), this.escortChip(), pay].filter((t) => t !== '');
      const lvBar = lp
        ? progressRow(`Lv ${lp.next}`, lp.have, lp.span, `${lp.rest.toLocaleString()} 남음`, `레벨 ${lp.next}까지`)
        : el('span', { class: 'dim' }, '레벨이 끝까지 올랐다');
      // 관등이 오르면 내 부대의 정원이 는다(`squadSizeFor`). 막대 끝에 적어야 전과를 쌓을 까닭이 하나 더 보인다. 동료
      // 분대의 정원은 그 동료의 관등이다(`mateSquadSize`) — 동료도 장수를 넘지 않으므로 장수의 관등이 천장이다.
      const rankBar = rp
        ? progressRow(
            rp.next.name,
            rp.have,
            rp.span,
            // 장수 줄에 붙는 막대라 "정원"이면 내 부대의 정원이다. "내 부대 정원"으로 늘렸더니 360에서 잘렸다(QA가 짚었다).
            `전과 ${rp.rest.toLocaleString()} 남음 · 정원 ${this.size}→${rp.size}`,
            `${rp.next.name}까지`,
          )
        : el('span', { class: 'dim' }, '관등이 끝까지 올랐다');
      return el(
        'div',
        { class: 'cmdr-row' },
        this.leaderCard(i),
        el(
          'div',
          { class: 'cmdr-txt' },
          el('b', {}, `${rank.name} · 장수 Lv ${lv}`),
          // 폰 너비에서는 들지 못한 배지가 말줄임된다(index.html) — 온전한 글을 `title`에 둔다. 월급은 줄이지 않는다(`pay`).
          chips.length > 0 ? el('span', { class: 'chips' }, ...chips.map((t) => el('span', { class: t === pay ? 'chip pay' : 'chip', title: t }, t))) : false,
          lvBar,
          rankBar,
        ),
      );
    }
    const c = this.companionAt(sq);
    if (!c) {
      return el(
        'div',
        { class: 'cmdr-row' },
        el('div', { class: 'vacant', 'aria-hidden': 'true' }, '지휘관 없음'),
        el(
          'div',
          { class: 'cmdr-txt' },
          el('b', {}, '지휘관이 없다'),
          el('span', {}, '병영의 부하장수 카드를 누르면 이 부대를 맡는다.'),
          el('span', { class: 'dim' }, '지휘관 없는 부대는 병사를 받지 않고 출정하지도 않는다.'),
        ),
      );
    }
    const level = companionLevelOf(this.army, c.id);
    // 동료의 관등과 그가 이끄는 정원(`mateSquadSize`). 막 합류하면 편장·14명이고, 함께 전과를 쌓으며 오른다.
    const rank = mateRank(this.army, c.id);
    return el(
      'div',
      { class: 'cmdr-row' },
      this.companionCard(c, i),
      el(
        'div',
        { class: 'cmdr-txt' },
        el('span', { class: 'k' }, '지휘'),
        el('b', {}, `${c.name} · ${c.title} · Lv ${level}`),
        el('span', {}, `관등 ${rank.name} · 정원 ${mateSquadSize(this.army, c.id)}명`),
        el('span', { class: 'bonus' }, captainText(c, level)),
        el('span', { class: 'dim' }, '카드를 병영으로 끌면 물러나고, 내 부대로 끌면 부관이 된다.'),
      ),
    );
  }

  /**
   * 장수 곁의 동료(부관)가 내 부대에 더하는 지휘 보정 칩. 곁에 아무도 없으면 빈 문자열. 처음에는 "곁의 온달: 휘하 공격
   * 18% 증가"라 적었는데 플레이한 사람이 "부관 온달"로 부르자고 했다. 짧은 이름표(탭·지휘 줄)도 같은 말을 쓴다.
   */
  private escortChip(): string {
    const c = this.escort ? COMPANIONS.find((x) => x.id === this.escort) : undefined;
    if (!c) return '';
    const b = captainShort(c, companionLevelOf(this.army, c.id));
    return b ? `부관 ${c.name} ${b}` : `부관 ${c.name}`;
  }

  /**
   * 칸의 "일괄 진급": 갈 곳이 하나뿐인 병종에서 레벨이 찬 병사를 한꺼번에 올린다(`batchPromotions`). 돈이
   * 모자라면 명단 앞에서부터 되는 만큼. 올릴 사람이 없으면 단추를 두지 않는다. 갈 곳이 둘 이상인 병사(민병 등)는
   * 사람이 골라야 하므로 카드 상세에서 올린다.
   */
  private batchButton(i: number, soldiers: readonly Soldier[]): HTMLElement | false {
    const list = batchPromotions(soldiers);
    if (list.length === 0) return false;
    const { count, cost } = batchAffordable(this.army, list);
    const label =
      count === 0
        ? `일괄 진급 ${list.length}명 · 돈 모자람`
        : count < list.length
          ? `일괄 진급 ${list.length}명 중 ${count}명 · ${cost}냥`
          : `일괄 진급 ${count}명 · ${cost}냥`;
    const b = withKey(
      button(label, () => {
        if (count === 0) return;
        let done = 0;
        for (const p of list.slice(0, count)) if (promote(this.army, p.uid, p.to.id)) done++;
        // cost는 계산치, done은 실제치다. 목록이 값 오름차순이고 그 순서로 올리므로 둘이 어긋나지 않는다.
        this.notice = `${done}명을 진급시켰다(${cost}냥).`;
        // 전부 올리면 단추가 사라진다. 키를 남겨야 포커스가 body로 떨어지지 않고 같은 칸의 탭이 받는다.
        this.focusKey = `batch:${i}`;
        this.changed();
        this.render();
      }, `mini batch${count === 0 ? ' off' : ''}`),
      `batch:${i}`,
    );
    b.dataset.hold = '';
    b.title = list.map((p) => `${soldierById(this.army, p.uid)?.name} → ${p.to.name}`).join(', ');
    if (count === 0) b.setAttribute('aria-disabled', 'true');
    return b;
  }

  /**
   * 갈 곳이 여럿인 병종의 일괄 진급: 병종마다 단추 하나("고구려 창수 6명 진급 ▾"). 누르면 작은 창에 갈 곳마다
   * "숙련 창수 6명 · 720냥" 단추가 있고, 그것을 눌러야 그 칸의 그 병종(레벨이 찬 병사)이 모두 그리로 오른다. 돈이
   * 모자라면 명단 앞에서부터(레벨 높은 병사부터) 되는 만큼, 하나도 안 되면 흐리게 막는다. 칸 머리의 단추 줄에 선다.
   *
   * 두 번 바꿨다. 처음에는 병종마다 한 줄에 갈 곳 단추를 늘어놓아 폰에서 칸 머리가 세 줄이 됐다(플레이한 사람이
   * 짚었다). 다음에는 고르기 상자(`<select>`)로 접었는데, 고르는 즉시 진급하니 PC에서 닫힌 상자에 화살표만 눌러도,
   * 아이폰 바퀴가 지나가기만 해도 change가 나 돈이 나갔다(리뷰가 짚었다). 창은 단추를 눌러야만 진급한다.
   * 갈 곳이 하나뿐인 병종은 일괄 진급(`batchButton`)이 맡는다. `branchingPromotions`에 까닭이 있다.
   */
  private branchButtons(i: number, soldiers: readonly Soldier[]): HTMLElement[] {
    return branchingPromotions(soldiers).map((g) => {
      const key = `bp:${i}:${g.from.id}`;
      const b = withKey(button(`${g.from.name} ${g.uids.length}명 진급 ▾`, () => this.openBranch(key, g), 'mini batch'), key);
      b.dataset.hold = '';
      b.setAttribute('aria-haspopup', 'dialog');
      return b;
    });
  }

  /** 갈 곳을 고르는 작은 창. 바깥·Esc·닫기로 닫고, 갈 곳을 누르면 진급하고 닫는다. */
  private openBranch(key: string, g: { from: TroopDef; uids: number[]; targets: TroopDef[] }): void {
    const n = g.uids.length;
    const dialog = el('dialog', { class: 'sheet bp-dialog', 'aria-labelledby': 'bp-title' }) as HTMLDialogElement;
    const close = (): void => dialog.close();
    const choices = g.targets.map((to) => {
      const { count, cost } = branchAffordable(this.army, n, to);
      const b = button(
        count === 0 ? `${to.name} · 돈 모자람 (${to.upgradeCost}냥씩)` : count < n ? `${to.name} ${n}명 중 ${count}명 · ${cost}냥` : `${to.name} ${n}명 · ${cost}냥`,
        () => {
          if (count === 0) return;
          let done = 0;
          for (const uid of g.uids.slice(0, count)) if (promote(this.army, uid, to.id)) done++;
          this.notice = `${g.from.name} ${done}명을 ${to.name}${josa(to.name, '으로', '로')} 진급시켰다(${done * to.upgradeCost}냥).`;
          // 다 올리면 이 단추가 사라진다. 키를 남기면 없을 때 같은 칸의 탭이 포커스를 받는다.
          this.focusKey = key;
          this.anchorHold = true;
          close();
          this.changed();
          this.render();
        },
        `promote bp-choice${count === 0 ? ' off' : ''}`,
      );
      if (count === 0) b.setAttribute('aria-disabled', 'true');
      return b;
    });
    const short = g.targets.some((to) => branchAffordable(this.army, n, to).count < n);
    dialog.append(
      el(
        'div',
        { class: 'sheet-in' },
        el('h3', { id: 'bp-title' }, `${g.from.name} ${n}명 진급`),
        el('p', { class: 'dim' }, `군자금 ${this.army.gold.toLocaleString()}냥${short ? ' · 모자라면 레벨 높은 병사부터 되는 만큼' : ''}`),
        el('div', { class: 'promos' }, ...choices),
        el('div', { class: 'sheet-actions' }, button('닫기', close, 'mini')),
      ),
    );
    // 바깥(어두운 곳)을 누르면 닫는다. 안쪽은 `sheet-in`이 다 덮는다.
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) close();
    });
    dialog.addEventListener('close', () => dialog.remove());
    (this.root.parentElement ?? this.root).append(dialog);
    dialog.showModal();
  }

  /** 그곳(분대·병영)에 진급할 수 있는(레벨과 돈이 다 되는) 병사가 있는가. 카드의 빨간 점과 같은 조건이다. */
  private promotableAt(p: Place): boolean {
    const list =
      p === 'barracks'
        ? this.barracksSoldiers()
        : this.squads[p].map((uid) => soldierById(this.army, uid)).filter((s): s is Soldier => !!s);
    return list.some((s) => promotionOptions(this.army, s).some((o) => o.ok));
  }

  /** 어느 부대에도 없는 병사. */
  private barracksSoldiers(): Soldier[] {
    const deployed = new Set(this.squads.flat());
    return this.army.soldiers.filter((s) => !deployed.has(s.uid));
  }

  /** 병영의 동료: 부대를 맡지 않은 동료(요양 중 포함). */
  private barracksCompanions(): CompanionDef[] {
    // 부관은 부대 자리에 없지만 병영에 있는 것도 아니다(내 부대에 선다).
    return metCompanions(this.army).filter((c) => !this.companions.includes(c.id) && c.id !== this.escort);
  }

  /** 병영 칸에 보이는 것, 카드 순서대로: 동료, 그리고 거르고 줄 세운 병사. 상세 넘겨 보기도 이 순서다. */
  private barracksShown(i: number): { all: Soldier[]; present: Branch[]; mates: CompanionDef[]; shown: Soldier[] } {
    const slot = this.slots[i];
    const all = this.barracksSoldiers();
    const present = BRANCH_ORDER.filter((b) => all.some((s) => TROOP_BY_ID.get(s.troop)!.branch === b));
    // 없는 가지를 골라 둔 채면(마지막 기병이 부대로 갔다든가) 전체로 돌아간다.
    if (slot.filter !== 'all' && !present.includes(slot.filter)) slot.filter = 'all';
    const shown = slot.filter === 'all' ? all : all.filter((s) => TROOP_BY_ID.get(s.troop)!.branch === slot.filter);
    return { all, present, mates: this.barracksCompanions(), shown: sortSoldiers(this.army, shown, slot.sort) };
  }

  private barracksBody(i: number): HTMLElement[] {
    const slot = this.slots[i];
    const { all, present, mates, shown } = this.barracksShown(i);

    const head = el(
      'div',
      { class: 'hhead' },
      el('h2', {}, '병영'),
      el('span', { class: 'cnt' }, `병사 ${all.length}명` + (mates.length > 0 ? ` · 부하장수 ${mates.length}명` : '')),
      el('span', { class: 'act' }, this.batchButton(i, all), ...this.branchButtons(i, all)),
    );

    const filters = el('div', { class: 'filters', role: 'group', 'aria-label': '가지로 거르기' });
    if (present.length > 1) {
      for (const b of ['all', ...present] as const) {
        const n = b === 'all' ? all.length : all.filter((s) => TROOP_BY_ID.get(s.troop)!.branch === b).length;
        const on = slot.filter === b;
        const chip = withKey(
          button(`${b === 'all' ? '전체' : BRANCH_NAMES[b]} ${n}`, () => {
            slot.filter = b;
            this.render();
          }, 'mini chip'),
          `filter:${i}:${b}`,
        );
        chip.setAttribute('aria-pressed', String(on));
        filters.append(chip);
      }
    }
    const sort = el('select', { class: 'sort', id: `sort-${i}`, 'aria-label': '줄 세우기' }) as HTMLSelectElement;
    for (const [v, label] of [['roster', '병종순'], ['level', '레벨순'], ['promo', '진급 먼저']] as const) {
      const o = el('option', { value: v }, label);
      if (slot.sort === v) o.setAttribute('selected', 'selected');
      sort.append(o);
    }
    sort.addEventListener('change', () => {
      slot.sort = sort.value as SortKey;
      this.render();
    });
    filters.append(sort);

    const cards = el('div', { class: 'cards' });
    // 동료는 병사 앞에. 부대를 맡길 사람이 병영에 있다는 것이 먼저 보여야 한다.
    for (const c of mates) cards.append(this.companionCard(c, i));
    for (const s of shown) cards.append(this.soldierCard(s, i));

    return [
      head,
      filters,
      all.length + mates.length === 0
        ? el('p', { class: 'hint' }, '병영이 비었다. 모집하면 여기로 들어온다.')
        : hinted(cards, 'cards-wrap', '카드'),
      isDestitute(this.army) &&
        el('p', { class: 'hint warn' }, `병사도 돈도 없다. ${CONSCRIPT_FLOOR}명까지는 값을 치르지 않고 긁어모을 수 있다.`),
      // 병사는 있는데 한 분대의 급료도 못 내는 바닥(`squadWageShort`). 공짜 징발은 사람이 모자랄 때만이라 여기서는 열리지
      // 않는다 — 노획을 철수할 때 확정하게 바꾸자(장수가 쓰러지면 쌓인 노획이 사라진다) 이 상태가 잦아졌다. 기준
      // 플레이어도 이 자리에서 빈 출정만 되풀이했다. 다시 일어서는 길을 짚어 준다.
      !isDestitute(this.army) &&
        this.squadWageShort() > 0 &&
        el(
          'p',
          { class: 'hint warn' },
          `군자금이 모자라 한 분대(${SQUAD_SIZE}명)의 급료도 낼 수 없다(${this.squadWageShort()}냥 모자람). 단신 토벌은 급료가 들지 않는다 — 장수 혼자 나가 군자금을 모으자. `,
          // 토벌 단추는 화면 맨 아래(`others`)에 있어 병영에서 보이지 않는다. 막힌 자리에서 바로 누를 수 있게 하나 더 둔다.
          withKey(button('혼자 나선다', () => this.onRaid(), 'mini'), 'raid-hint'),
        ),
    ].filter((n): n is HTMLElement => n instanceof HTMLElement);
  }

  // ------------------------------------------------------------------
  // 카드

  /**
   * 병사 카드: 그림, 이름, 레벨. 진급할 수 있으면(레벨과 돈이 다 되면) 오른쪽 위에 빨간 점.
   * 병종·경험치는 상세(누르기)에 있다 — 작은 카드에 다 적으면 이름이 잘렸다.
   */
  private soldierCard(s: Soldier, slot: number): HTMLElement {
    const t = TROOP_BY_ID.get(s.troop)!;
    const level = levelProgress(s.xp).level;
    const where = placeOf(this.plan, s.uid);
    const canUp = promotionOptions(this.army, s).some((o) => o.ok);
    const elite = isElite(s);
    const card = withKey(el('button', { type: 'button', class: `ucard${elite ? ' elite' : ''}` }), `c:${slot}:s${s.uid}`);
    card.dataset.hold = '';
    if (where !== 'barracks') card.style.setProperty('--cc', `var(--sq${where})`);
    card.title = `${elite ? '용사 ' : ''}${s.name} · Lv ${level} T${t.tier} ${t.name}${canUp ? ' · 진급할 수 있다' : ''}${s.locked ? ' · 잠금(자동 편성이 옮기지 않는다)' : ''}\n누르면 상세. 끌어서 다른 칸에 옮기거나, 같은 부대 카드 위에 놓아 순서를 바꾼다`;
    card.append(
      unitIcon(iconKeyFor(specForTroop(s.troop))),
      el('span', { class: 'nm' }, s.name),
      el('span', { class: 'lv' }, `Lv${level}`),
    );
    if (canUp) card.append(el('span', { class: 'dot', role: 'img', 'aria-label': '진급할 수 있다' }));
    if (s.locked) card.append(lockMark());
    if (elite) {
      // 병영의 용사는 아직 지킬 사람이 없다 — "호위 … 싸운다"고 적으면 틀린 말이다(QA).
      const who = this.wardName(s);
      card.title += who === null ? '\n부대에 넣으면 그 부대의 대장을 호위한다' : `\n${who} 호위 — 분대의 명령 대신 그 곁에서 싸운다`;
    }
    hookCard(
      card,
      () => this.openDetail(slot, `s${s.uid}`),
      this.cardDrag(
        where,
        false,
        `c:${slot}:s${s.uid}`,
        (to, near) => this.moveSoldierTo(s, slot, to, near),
        (target) => {
          if (!reorderSoldier(this.plan, s.uid, target)) return;
          this.arrivedKey = `c:${slot}:s${s.uid}`;
          this.focusKey = this.arrivedKey;
          this.changed();
          this.render();
        },
      ),
    );
    return card;
  }

  /** 장수·동료의 그림 열쇠: 착장을 입힌 몸으로 고른다 — 림월드식이면 입은 갑옷·투구·무기·말이 그림에 보인다. */
  private leaderKey(): string | null {
    return iconKeyFor(dressLeader(this.army.culture, loadoutOf(this.army, HERO), heroLevelOf(this.army)));
  }

  private companionKey(c: CompanionDef): string | null {
    return iconKeyFor(dressCompanion(c, companionLevelOf(this.army, c.id), loadoutOf(this.army, c.id), this.army.culture));
  }

  private leaderCard(slot: number): HTMLElement {
    const lv = heroLevelOf(this.army);
    const card = withKey(el('button', { type: 'button', class: 'ucard hero' }), `c:${slot}:hero`);
    card.dataset.hold = '';
    card.title = '장수 — 내 부대를 떠나지 않는다. 누르면 상세';
    card.append(
      unitIcon(this.leaderKey()),
      el('span', { class: 'nm' }, '장수'),
      el('span', { class: 'lv' }, `Lv${lv}`),
    );
    // 장수는 끌 수 없다. 끌기를 주지 않으면 집히지 않는다.
    hookCard(card, () => this.openDetail(slot, 'hero'), null);
    return card;
  }

  private companionCard(c: CompanionDef, slot: number): HTMLElement {
    const st = this.army.companions.find((x) => x.id === c.id);
    const restLeft = st ? st.readyAt - this.army.sortieCount : 0;
    // 영토의 수비 대장(Todo9)이면 요양처럼 자리를 맡지 못한다. 대장은 영토 화면에서 내린다.
    const guard = st?.garrison ? TERRITORY_BY_ID.get(st.garrison) : undefined;
    const resting = restLeft > 0 || guard !== undefined;
    const card = withKey(el('button', { type: 'button', class: `ucard hero${resting ? ' resting' : ''}` }), `c:${slot}:h${c.id}`);
    card.dataset.hold = '';
    card.title = guard
      ? `${c.name} — ${guard.name} 수비 대장. 영토 화면에서 내리면 돌아온다`
      : restLeft > 0
        ? `${c.name} — 요양 중, 출정 ${restLeft}번 뒤 돌아온다`
        : `${c.name} · ${c.title}\n누르면 상세, 끌어서 옮긴다`;
    card.append(
      unitIcon(this.companionKey(c)),
      el('span', { class: 'nm' }, c.name),
      el('span', { class: 'lv' }, guard ? '수비' : restLeft > 0 ? `요양${restLeft}` : `Lv${companionLevelOf(this.army, c.id)}`),
    );
    if (st?.locked) card.append(lockMark());
    // 요양 중인 동료는 끌 수 없다(자리를 맡을 수 없다). 상세에 까닭이 있다. 장수 곁의 동료는 내 부대에서 끈다.
    const seat = this.escort === c.id ? 0 : this.companions.indexOf(c.id);
    hookCard(
      card,
      () => this.openDetail(slot, `h${c.id}`),
      resting ? null : this.cardDrag(seat >= 0 ? seat : 'barracks', true, `c:${slot}:h${c.id}`, (to) => this.moveCompanionTo(c, slot, to)),
    );
    return card;
  }

  // ------------------------------------------------------------------
  // 상세(누르기)

  /**
   * 열린 상세 창: 어느 칸의 누구(`hero`·`h동료id`·`s병사uid`)를 보고 있나.
   *
   * 창은 화면(`#screen`)에 붙인다 — `render`가 지우는 `root` 밖이다. 진급한 뒤에도 창을 닫지 않고
   * 그 병사를 계속 보여 달라고 했다(플레이한 사람). 처음에는 창을 `root`에 붙여, 진급으로 화면을 다시
   * 그리면 창도 함께 지워졌다.
   */
  private sheet: { dialog: HTMLDialogElement; body: HTMLElement; slot: number; id: string } | null = null;
  /** 상세 창을 다시 채운 뒤 초점을 받을 단추의 열쇠(창 안에서 찾는다). */
  private sheetFocus: string | null = null;

  /**
   * 칸에 보이는 카드 순서 그대로의 상세 목록. 휙 넘겨 보기가 이 순서를 따른다 — 화면에서 옆에 있던
   * 병사가 다음에 나와야 한다.
   */
  private sheetOrder(slot: number): string[] {
    const p = this.slots[slot].place;
    if (p === 'barracks') {
      const { mates, shown } = this.barracksShown(slot);
      return [...mates.map((c) => `h${c.id}`), ...shown.map((x) => `s${x.uid}`)];
    }
    // 부관은 내 부대 목록에 선다(장수 다음).
    const esc = this.escort;
    const cmdr = p === 0 ? ['hero', ...(esc ? [`h${esc}`] : [])] : this.companions[p] ? [`h${this.companions[p]}`] : [];
    return [...cmdr, ...this.squadSoldiers(p).map((x) => `s${x.uid}`)];
  }

  /** 상세 창을 연다(이미 열려 있으면 내용만 바꾼다). 바깥(어두운 곳)을 누르거나 Esc로 닫힌다. */
  private openDetail(slot: number, id: string): void {
    if (!this.sheet) {
      const body = el('div', { class: 'sheet-in' });
      const dialog = el('dialog', { class: 'sheet', tabindex: '-1', 'aria-labelledby': 'sheet-title' }, body) as HTMLDialogElement;
      // 옆으로 끌어 넘긴 직후의 click은 삼킨다(잡기 단계). 브라우저는 누른 곳과 뗀 곳의 공통 조상에 click을
      // 내므로, 마우스로 넓은 진급 단추 안에서 끌면 넘기기와 함께 진급이 일어났고, 안에서 끌어 바깥에서
      // 떼면 창이 닫혔다(리뷰가 짚었다).
      //
      // 삼키는 것은 **넘기기가 낸 click 하나**뿐이다. 처음에는 넘긴 뒤 400ms 안의 click을 모두 삼켜, 넘기고 곧바로
      // 같은 자리의 진급 단추를 톡 누르면 첫 톡이 먹혔다 — 두 번 눌러야 했다(플레이한 사람이 짚었다). 새 손가락이
      // 닿으면(`pointerdown`) 깃발을 내린다: 그 뒤 click은 사람이 누른 것이다.
      let swallowNext = false;
      dialog.addEventListener(
        'click',
        (e) => {
          if (!swallowNext) return;
          swallowNext = false;
          // 같은 노드(대화상자)의 바깥 닫기 처리기까지 막는다.
          e.stopImmediatePropagation();
          e.preventDefault();
        },
        true,
      );
      // 바깥을 누르면 닫는다. 안쪽은 `sheet-in`이 다 덮으므로 대화상자 자체가 눌리는 것은 바깥뿐이다.
      // 누른 곳도 바깥이어야 한다 — 안에서 끌어 바깥에서 떼도 click은 대화상자에 떨어진다.
      let downOutside = false;
      dialog.addEventListener('pointerdown', (e) => {
        downOutside = e.target === dialog;
        swallowNext = false;
      });
      dialog.addEventListener('click', (e) => {
        // 바깥에서 시작해 바깥에서 끝난 옆 끌기는 넘기기다. 그 click은 잡기 단계가 stopImmediatePropagation으로 막는다.
        if (e.target === dialog && downOutside) dialog.close();
      });
      dialog.addEventListener('close', () => {
        dialog.remove();
        this.sheet = null;
      });
      dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.stepDetail(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.stepDetail(1);
        }
      });
      // 옆으로 넘기는 손가락은 세로 굴림도 막는다. 창은 세로로 굴러가는 칸이라, 휙 넘길 때 섞인 세로 몫으로
      // 관성 굴림(fling)이 시작됐고, 안드로이드 브라우저는 관성 굴림 중의 첫 탭을 멈추는 데만 쓰고 click을 내지
      // 않는다 — 넘긴 뒤 진급 단추를 두 번 눌러야 했다(플레이한 사람이 짚었다). 첫 움직임으로 방향을 정하고,
      // 옆이면 그 손가락의 touchmove를 모두 막는다. 넘기기 판정은 아래 포인터 이벤트가 그대로 한다.
      let tx0 = 0;
      let ty0 = 0;
      let sideways: boolean | null = null;
      dialog.addEventListener(
        'touchstart',
        (e) => {
          tx0 = e.touches[0].clientX;
          ty0 = e.touches[0].clientY;
          sideways = null;
        },
        { passive: true },
      );
      dialog.addEventListener(
        'touchmove',
        (e) => {
          const t = e.touches[0];
          if (sideways === null) {
            const ax = Math.abs(t.clientX - tx0);
            const ay = Math.abs(t.clientY - ty0);
            if (ax + ay < 6) return;
            sideways = ax > ay;
          }
          if (sideways && e.cancelable) e.preventDefault();
        },
        { passive: false },
      );
      // 휙 넘기기: 옆으로 50px 넘게, 세로보다 확실히 옆으로 끌면 이전·다음 병사. 창 안을 세로로 굴리는
      // 손가락과 섞이지 않게 옆 움직임이 세로의 1.5배를 넘어야 한다.
      let x0 = 0;
      let y0 = 0;
      let down = false;
      dialog.addEventListener('pointerdown', (e) => {
        down = true;
        x0 = e.clientX;
        y0 = e.clientY;
      });
      dialog.addEventListener('pointerup', (e) => {
        if (!down) return;
        down = false;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          swallowNext = true;
          this.stepDetail(dx < 0 ? 1 : -1);
        }
      });
      dialog.addEventListener('pointercancel', () => {
        down = false;
      });
      (this.root.parentElement ?? this.root).append(dialog);
      this.sheet = { dialog, body, slot, id };
      dialog.showModal();
    }
    this.sheet.slot = slot;
    this.sheet.id = id;
    this.fillDetail();
  }

  /** 칸의 카드 순서에서 앞(-1)·뒤(+1) 사람으로. 끝에서는 멈춘다. */
  private stepDetail(d: number): void {
    if (!this.sheet) return;
    const order = this.sheetOrder(this.sheet.slot);
    const at = order.indexOf(this.sheet.id);
    const next = order[at + d];
    if (at < 0 || next === undefined) return;
    this.sheet.id = next;
    this.fillDetail(d > 0 ? 'next' : 'prev');
  }

  /** 지금 보는 사람으로 창을 다시 채운다. 그 사람이 칸에서 사라졌으면(옮겼다) 닫는다. */
  private fillDetail(from?: 'next' | 'prev'): void {
    const sh = this.sheet;
    if (!sh) return;
    const order = this.sheetOrder(sh.slot);
    const at = order.indexOf(sh.id);
    if (at < 0) {
      sh.dialog.close();
      return;
    }
    // 넘겨 보기 ‹ n / N ›는 닫기와 같은 아래 줄 왼쪽에 둔다. 처음에는 창 맨 위 한 줄을 따로 차지했다 — 창을
    // 줄여 닫기가 굴리지 않고 보이게 하려고 내렸다(플레이한 사람이 청했다). 그 자리에 있던 순서 앞·뒤 단추는
    // 없앴다: 순서는 카드를 끌어 바꾼다.
    const nav = el(
      'div',
      { class: 'sheet-nav', role: 'group', 'aria-label': '넘겨 보기 — 옆으로 밀어도 된다' },
      withNav(button('‹', () => this.stepDetail(-1), 'mini'), '앞 사람', at === 0),
      el('span', { class: 'dim' }, `${at + 1} / ${order.length}`),
      withNav(button('›', () => this.stepDetail(1), 'mini'), '다음 사람', at === order.length - 1),
    );
    const id = sh.id;
    const content =
      id === 'hero'
        ? this.leaderDetail()
        : id.startsWith('h')
          ? this.companionDetail(COMPANIONS.find((c) => c.id === id.slice(1))!)
          : this.soldierDetail(soldierById(this.army, Number(id.slice(1)))!, sh.slot);
    const hadFocus = sh.dialog.contains(document.activeElement);
    // 내용의 마지막은 단추 줄(`sheet-actions`)이다. 그 앞머리에 넘겨 보기를 넣는다. 누가 상세 끝에 다른 것을
    // 덧붙이면 넘겨 보기가 그 안으로 들어가므로, 단추 줄이 아니면 따로 한 줄로 둔다(리뷰가 권했다).
    const actions = content[content.length - 1];
    if (actions?.classList.contains('sheet-actions')) actions.prepend(nav);
    else content.push(el('div', { class: 'sheet-actions' }, nav));
    sh.body.replaceChildren(...content);
    sh.body.classList.remove('from-next', 'from-prev');
    if (from) {
      void sh.body.offsetWidth;
      sh.body.classList.add(`from-${from}`);
    }
    // 누른 단추가 새 내용으로 갈려 나가면 포커스가 body로 떨어져 화살표 키가 창에 닿지 않는다. ‹ ›로
    // 넘겼으면 새 내용의 같은 단추로 — 이어서 누를 수 있게. 끝이라 못 누르면 창 자체로.
    if (hadFocus && !sh.dialog.contains(document.activeElement)) {
      // 진급으로 다시 채웠으면 첫 진급 단추로 — 키보드로 이어 올릴 수 있게(리뷰가 권했다).
      // 잠금 단추를 눌렀으면 새 잠금 단추로. 처음에는 화면의 열쇠 찾기(`focusKey`)에 맡겼는데 그것은 화면 안만 찾고
      // 창은 화면 밖에 붙어 있다 — 초점이 첫 진급 단추로 가, 이어 누른 Enter가 잠금을 푸는 대신 진급을 시켰다(리뷰).
      const wanted = this.sheetFocus ? sh.body.querySelector<HTMLElement>(`[data-key="${this.sheetFocus}"]`) : null;
      const same = wanted
        ? wanted
        : from
          ? (nav.children[from === 'next' ? 2 : 0] as HTMLElement)
          : sh.body.querySelector<HTMLElement>('.promos button:not(.off)');
      if (same && !same.classList.contains('off')) same.focus({ preventScroll: true });
      else sh.dialog.focus({ preventScroll: true });
    }
    this.sheetFocus = null;
  }

  /** 창 머리: 그림, 이름, 짧은 줄 몇(병사는 둘, 방패를 들면 셋). */
  private detailTop(portraitKey: string, title: string, sub: string[]): HTMLElement {
    const canvas = el('canvas', { class: 'sheet-portrait', width: '0', height: '0' }) as HTMLCanvasElement;
    // 두 배. 세 배였을 때는 그림 하나가 창의 5분의 1을 차지해 닫기가 화면 밖으로 밀렸다.
    void drawUnitPortrait(canvas, portraitKey, 2);
    return el(
      'div',
      { class: 'sheet-top' },
      canvas,
      el('div', { class: 'sheet-name' }, el('h3', { id: 'sheet-title' }, title), ...sub.map((t) => el('span', { class: 'dim' }, t))),
    );
  }

  /**
   * 병사 상세의 용사 칸(`ELITE`). 최고 병종의 병사에게만 보인다 — 그 아래 병사에게는 아직 먼 이야기라 창을 늘리지 않는다.
   * 세울 수 있으면 채운 단추, 못 세우면 까닭을 적은 흐린 줄(진급 단추와 같은 꼴). 용사는 물릴 수 있다 — 낸 돈은 돌아오지
   * 않는다고 단추 곁에 적는다.
   */
  private eliteBox(s: Soldier, slot: number): HTMLElement[] {
    const t = TROOP_BY_ID.get(s.troop)!;
    if (t.tier < ELITE.tier) return [];
    const have = elitesOf(this.army).length;
    const cap = eliteCap(this.army);
    const head = el('h4', {}, `용사 · ${have}/${cap}명`);
    const redraw = (msg: string): void => {
      this.notice = msg;
      this.arrivedKey = `c:${slot}:s${s.uid}`;
      this.sheetFocus ??= 'elite';
      this.anchorHold = true;
      this.changed();
      this.render();
    };
    if (s.elite === true) {
      const off = withKey(
        button('용사에서 물린다', () => {
          if (dismissElite(this.army, s.uid)) redraw(`${s.name}${josa(s.name, '을', '를')} 용사에서 물렸다.`);
        }, 'mini'),
        'elite',
      );
      off.title = '물리면 여느 병사의 몸으로 돌아간다. 세울 때 낸 돈은 돌아오지 않는다.';
      return [
        head,
        el('p', { class: 'dim' }, `체력 +${ELITE.hpPct - 100}% · 공격 +${ELITE.atkPct - 100}%. 이긴 판에서 쓰러져도 열에 ${ELITE.woundedPct / 10}은 다쳐서 돌아온다.`),
        el('p', { class: 'dim' }, this.wardLine(s)),
        el('div', { class: 'promos' }, off),
      ];
    }
    const chk = eliteCheck(this.army, s);
    const cost = eliteCost(s);
    const b = button(
      '',
      () => {
        if (!chk.ok) return;
        if (appointElite(this.army, s.uid)) redraw(`${s.name}${josa(s.name, '을', '를')} 용사로 세웠다(${cost}냥).`);
      },
      `promote${chk.ok ? '' : ' off'}`,
    );
    const why = chk.ok ? '' : chk.why;
    b.append(
      el('span', { class: 'pname' }, ...(chk.ok ? [el('span', { 'aria-hidden': 'true' }, '★ '), '용사로 세운다'] : ['용사로 세운다'])),
      el(
        'span',
        { class: 'pcost' },
        why === 'level' ? `Lv ${ELITE.level}부터` : why === 'full' ? `자리 없음 (${have}/${cap})` : why === 'gold' ? `${cost}냥 · 모자람` : `${cost}냥`,
      ),
    );
    if (!chk.ok) {
      b.setAttribute('aria-disabled', 'true');
      b.title =
        why === 'level'
          ? `레벨 ${ELITE.level}부터 용사로 세울 수 있다`
          : why === 'full'
            ? `용사의 자리가 찼다(${have}/${cap}). 관등이 오르면 늘어난다 — 지금 용사를 물려도 된다`
            : `돈이 모자란다 (${cost}냥)`;
    }
    return [
      head,
      el('p', { class: 'dim' }, `오래 살아남은 최고 병종의 병사를 가려 세운다. 체력 +${ELITE.hpPct - 100}% · 공격 +${ELITE.atkPct - 100}%, 이긴 판에서 쓰러져도 열에 ${ELITE.woundedPct / 10}은 돌아온다(여느 병사는 셋). 급료는 한 배 반. 용사는 제 부대의 대장을 호위한다 — 분대의 명령 대신 대장 곁에서 싸운다.`),
      el('div', { class: 'promos' }, withKey(b, 'elite')),
    ];
  }

  /**
   * 이 용사가 호위하는 사람의 이름: "장수", "온달". **제가 든 부대의 대장**이다 — 내 부대면 장수, 부하 부대면 그
   * 부하장수(플레이한 사람: "용사가 소속한 부대의 대장(장수, 혹은 부하장수)를 호위하면 돼"). 병영에 있으면 아직 없다(null).
   * 처음에는 용사마다 고르는 상자가 있었다(`Soldier.ward`).
   */
  private wardName(s: Soldier): string | null {
    const p = placeOf(this.plan, s.uid);
    if (p === 'barracks') return null;
    return p === 0 ? '장수' : (this.companionAt(p)?.name ?? '장수');
  }

  /** 용사 칸의 호위 안내 한 줄. 지킬 사람을 바꾸려면 부대를 옮긴다. */
  private wardLine(s: Soldier): string {
    const who = this.wardName(s);
    return who === null
      ? '부대에 넣으면 그 부대의 대장을 호위한다. 분대의 명령은 따르지 않는다(후퇴는 따른다).'
      : `${who} 호위 — 제 부대의 대장 곁에서 둘레의 적만 친다. 분대의 명령은 따르지 않는다(후퇴는 따른다). 다른 사람을 지키게 하려면 부대를 옮긴다.`;
  }

  /**
   * 상세 창의 잠금 단추(병사·동료). 켜면 자동 편성·모두 병영으로·편성 비우기가 그 사람을 옮기지 않는다(`locked`). 누른
   * 상태는 `aria-pressed`로 알리고, 창은 그대로 둔 채 뒤의 카드에 자물쇠가 붙는다.
   *
   * 글은 켜든 끄든 두 글자다('잠금'·'잠김'). '잠금 풀기'로 늘렸더니 360px 폰에서 단추 줄이 두 줄이 되어 닫기가 아랫줄로
   * 내려가고 창이 50px 늘었다 줄었다 했다(QA).
   */
  private lockToggle(who: { locked?: true }, name: string, where: string): HTMLElement {
    const on = who.locked === true;
    const b = button(
      '',
      () => {
        setSoldierLock(who, !on);
        this.notice = on ? `${name}의 잠금을 풀었다.` : `${name}${josa(name, '을', '를')} 잠갔다. 자동 편성을 눌러도 ${where}에 남는다.`;
        // 다시 그린 창에서 이 단추로 초점을 돌린다(`fillDetail`) — 이어서 눌러 풀 수 있게.
        this.sheetFocus = 'lock';
        this.anchorHold = true;
        this.changed();
        this.render();
      },
      `mini lock-toggle${on ? ' on' : ''}`,
    );
    b.append(lockIcon(), on ? '잠김' : '잠금');
    b.setAttribute('aria-pressed', String(on));
    b.setAttribute('aria-label', `잠금 — ${name}`);
    b.title = on ? '잠겨 있다 — 자동 편성이 옮기지 않는다. 누르면 푼다.' : '잠그면 자동 편성을 눌러도 지금 자리에 남는다. 손으로는 옮길 수 있다.';
    return withKey(b, 'lock');
  }

  private detailClose(): HTMLElement {
    return button('닫기', () => this.sheet?.dialog.close(), 'mini');
  }

  private soldierDetail(s: Soldier, slot: number): HTMLElement[] {
    const t = TROOP_BY_ID.get(s.troop)!;
    const p = levelProgress(s.xp);
    const opts = promotionOptions(this.army, s);
    const promos = el('div', { class: 'promos' });
    if (opts.length === 0) promos.append(el('span', { class: 'dim' }, '가장 높은 병종이다.'));
    for (const o of opts) {
      const b = button(
        '',
        () => {
          if (!o.ok) return;
          promote(this.army, s.uid, o.to.id);
          // 병영을 가지로 거른 채 진급해 다른 가지가 되면(창수 → 기병) 거르기에 걸려 창이 닫혔다. 창을
          // 그대로 두기로 했으므로 거르기를 푼다(리뷰가 짚었다).
          const here = this.slots[slot];
          if (here.place === 'barracks' && here.filter !== 'all' && here.filter !== o.to.branch) here.filter = 'all';
          this.notice = `${s.name}${josa(s.name, '이', '가')} ${o.to.name}${josa(o.to.name, '으로', '로')} 진급했다.`;
          this.arrivedKey = `c:${slot}:s${s.uid}`;
          // 창은 그대로 두고(플레이한 사람이 청했다) 뒤의 화면만 새로 그린다. 화면은 움직이지 않는다.
          this.anchorHold = true;
          this.changed();
          this.render();
        },
        `promote${o.ok ? '' : ' off'}`,
      );
      // 누를 수 있는 진급은 "▲ ○○로 진급 [값]"으로 채운 단추, 못 누르면 까닭을 적은 흐린 줄. 처음에는 셋 다
      // 옅은 초록 상자에 "창수 (T2) · 40냥"만 적혀 단추로 보이지 않았다(플레이한 사람이 짚었다).
      const name = `${o.to.name}(T${o.to.tier})${josa(o.to.name, '으로', '로')} 진급`;
      b.append(
        // ▲는 장식이다. 화면 낭독기가 "검은 위쪽 삼각형"을 읽지 않게 뗀다.
        el('span', { class: 'pname' }, ...(o.ok ? [el('span', { 'aria-hidden': 'true' }, '▲ '), name] : [name])),
        el(
          'span',
          { class: 'pcost' },
          o.why === 'level' ? `Lv ${t.levelToUpgrade}부터` : o.why === 'gold' ? `${o.to.upgradeCost}냥 · 모자람` : `${o.to.upgradeCost}냥`,
        ),
      );
      // 못 누르는 단추라는 것을 보조기술에도 알린다. `disabled`를 쓰지 않는 것은 까닭을 단
      // title이 떠야 해서다 — 비활성 단추는 몇몇 브라우저에서 툴팁을 띄우지 않는다.
      if (!o.ok) {
        b.setAttribute('aria-disabled', 'true');
        b.title = o.why === 'level' ? `레벨 ${t.levelToUpgrade}부터 진급할 수 있다` : `돈이 모자란다 (${o.to.upgradeCost}냥)`;
      }
      promos.append(b);
    }
    return [
      this.detailTop(iconKeyFor(specForTroop(s.troop)) ?? '', isElite(s) ? `용사 ${s.name}` : s.name, [
        `T${t.tier} ${t.name} · ${this.placeName(placeOf(this.plan, s.uid))}${isElite(s) && this.wardName(s) !== null ? ` · ${this.wardName(s)} 호위` : ''}`,
        p.span > 0 ? `Lv ${p.level} · 다음 레벨까지 ${p.span - p.into}` : `Lv ${p.level} · 최고 레벨`,
        // 방패를 드는 병사(창)는 머리에 적는다. 그림에 방패가 없고 설명은 창을 내려야 보여, 방패가 있는 줄 알 수 없었다
        // (플레이한 사람이 신라 창병의 창을 보고 "방패가 있다는 사실을 알 수 없어"라고 했다).
        ...(shieldFor(t.branch, t.tier) > 0 ? [`방패 — 앞에서 오는 화살 ${shieldFor(t.branch, t.tier)}발을 막는다`] : []),
      ]),
      soldierStatCard(s),
      el('h4', {}, `진급 · 군자금 ${this.army.gold.toLocaleString()}냥`),
      promos,
      ...this.eliteBox(s, slot),
      el('div', { class: 'sheet-actions' }, this.lockToggle(s, s.name, this.placeName(placeOf(this.plan, s.uid))), this.detailClose()),
    ];
  }

  private leaderDetail(): HTMLElement[] {
    const a = this.army;
    const raid = Math.min(a.raidXp, RAID_XP_CAP);
    return [
      this.detailTop(
        this.leaderKey() ?? '',
        '장수',
        [`${rankFor(a.totalMerit).name} · 누적 전과 ${a.totalMerit.toLocaleString()}`, raid > 0 ? `토벌 경험치 ${raid} / ${RAID_XP_CAP}` : ''].filter(Boolean),
      ),
      leaderStatCard(a),
      // 장수 상세에서 누르면 장비 화면이 장수를 골라 둔 채 열린다(플레이한 사람이 청했다).
      el('div', { class: 'sheet-actions' }, button('장비', () => this.onArmory(HERO), 'mini'), this.detailClose()),
    ];
  }

  private companionDetail(c: CompanionDef): HTMLElement[] {
    const lore = COMPANION_LORE[c.id];
    const st = this.army.companions.find((x) => x.id === c.id);
    const resting = st !== undefined && st.readyAt > this.army.sortieCount;
    const guard = st?.garrison ? TERRITORY_BY_ID.get(st.garrison) : undefined;
    return [
      this.detailTop(this.companionKey(c) ?? '', c.name, [lore ? `${lore.who} · ${c.title}` : c.title]),
      ...(lore !== undefined ? [el('p', { class: 'lore' }, lore.story)] : []),
      companionStatCard(this.army, c),
      // 요양 중이면 자리를 맡을 수 없다(끌리지도 않는다). 까닭을 적는다.
      ...(resting ? [el('p', { class: 'dim' }, `요양 중이다. 출정 ${st!.readyAt - this.army.sortieCount}번 뒤 돌아온다.`)] : []),
      ...(guard ? [el('p', { class: 'dim' }, `${guard.name}의 수비 대장이다. 영토 화면에서 대장을 내리면 출정에 나설 수 있다.`)] : []),
      el(
        'div',
        { class: 'sheet-actions' },
        // 계정에 없는 동료(아직 만나지 않았다)는 잠글 것이 없다.
        ...(st ? [this.lockToggle(st, c.name, this.escort === c.id ? '부관 자리' : this.placeName(this.companions.indexOf(c.id) > 0 ? this.companions.indexOf(c.id) : 'barracks'))] : []),
        button('장비', () => this.onArmory(c.id), 'mini'),
        this.detailClose(),
      ),
    ];
  }

  // ------------------------------------------------------------------
  // 아래: 출정 막대와 그 밖의 일

  /** 지금 열린 분대의 정원 합. 분대마다 그 지휘관(장수·동료)의 관등이 정원이다(`sizeOf`). */
  private capacity(): number {
    let n = 0;
    for (let sq = 0; sq < MAX_SQUADS; sq++) if (this.isOpen(sq)) n += this.sizeOf(sq);
    return n;
  }

  /**
   * 늘 보이는 출정 막대: 정원 게이지 한 줄, 그 아래 단추 한 줄(자동 편성·모집·출발 깊이·출정). 스크롤을
   * 어디까지 내리든 화면 아래에 붙어 있다(폰에서는 엄지 자리).
   *
   * 처음에는 알림 줄·굵은 게이지·깊이 칩 줄·모집 두 단추 줄·출정 줄이 쌓여 폰 화면의 3분의 1(225px)을
   * 먹었다(플레이한 사람이 짚었다). 그래서 게이지는 얇게 줄이고 숫자는 옆에 적고, 깊이 칩 줄은 단추 하나
   * (고르기 상자)로, "정원 가득"처럼 누를 수 없는 단추는 없앴다. 부족분 고용은 정원이 빌 때만 한 줄로 뜬다.
   * 알림은 막대 위에 떠서 자리를 먹지 않는다 — 막대가 출렁이지도 않는다.
   */
  private actionBar(notice: string | null): HTMLElement {
    const n = this.deployedCount();
    const cmd = this.capacity();
    const over = n > cmd;
    const pct = Math.min(100, Math.round((n / Math.max(1, cmd)) * 100));
    // 급료(`wageFor`, Todo6)는 떠날 때 먼저 낸다. 단추에 적어야 데려가는 인원이 돈이라는 것이 보인다. 모자라면 못 떠난다.
    const wage = wageFor(this.army, this.squads.flat());
    const broke = n > 0 && wage > this.army.gold;
    // **급료가 모자라면 이 단추가 단신 토벌이 된다.** 처음에는 흐린 "급료 모자람 · N냥"으로 막기만 했고, 길은 화면 맨 아래
    // '그 밖의 일'의 작은 "혼자 나선다"였다 — 플레이한 사람: "급료 모자름일 때 혼자 나선다를 선택해야 한다는 사실을 잘
    // 발견하기 힘든거 같아. 이 상황일 때는 출정 버튼을 아예 혼자 나선다 버튼으로 바꾸면 어때?" 막힌 자리에 길을 둔다.
    // 병사를 줄여 나가는 길은 그대로다(줄이면 단추가 출정으로 돌아온다) — 모자란 돈을 단추에 적어 둘을 견주게 한다.
    const short = wage - this.army.gold;
    const go = broke
      ? button('', () => this.onRaid(), 'primary go solo')
      : button(n === 0 ? '병력을 고르시오' : `출정 ${n}명 · 급료 ${wage}냥`, () => this.start(), `primary go${n === 0 ? ' off' : ''}`);
    if (n === 0) go.setAttribute('aria-disabled', 'true');
    const d = this.startDepth();
    if (broke) {
      go.append(
        el('span', { class: 'go-main' }, '혼자 나선다'),
        el('span', { class: 'go-sub' }, `급료 ${short.toLocaleString()}냥 모자람`),
        // 보이지 않는 자: 출정일 때의 글. 단추의 폭을 출정일 때와 같게 잡아 막대의 줄 나눔이 달라지지 않게 한다 — 없으면
        // 360px 폰에서 좁아진 단추가 윗줄로 올라붙어 막대가 32px 줄었다가, 병사를 줄여 출정으로 돌아오면 도로 늘었다(QA).
        el('span', { class: 'go-size', 'aria-hidden': 'true' }, `출정 ${n}명 · 급료 ${wage}냥`),
      );
      go.title = `급료 ${wage}냥에 ${short}냥이 모자라다. 장수 혼자 도적떼를 쳐 군자금을 번다(단신 토벌 — 급료가 들지 않는다). 병사를 줄이면 출정할 수 있다.`;
      go.setAttribute('aria-label', `혼자 나선다, 단신 토벌. 급료 ${wage}냥에 ${short}냥 모자람`);
    } else if (n > 0 && d > 1) {
      // 접근성 이름은 보이는 글을 덮으므로 급료도 함께 싣는다(처음에는 빠져 낭독기가 비용을 읽지 않았다 — 리뷰가 짚었다).
      go.setAttribute('aria-label', `출정 ${n}명, 급료 ${wage}냥, 깊이 ${d}부터${d > this.army.deepest ? ', 처음 가는 깊이' : ''}`);
    }
    return el(
      'div',
      { class: 'sortie-bar', role: 'region', 'aria-label': '출정' },
      // 알림이 없으면 비어 있고 CSS가 감춘다. 있으면 막대 위에 떠서 잠시 뒤 흐려진다.
      el('p', { class: 'bar-notice', role: 'status' }, notice),
      el(
        'div',
        { class: 'bar-gauge' },
        el(
          'div',
          { class: 'gauge', 'aria-hidden': 'true' },
          el('div', { class: `gauge-fill${over ? ' over' : ''}`, style: `width:${pct}%` }),
        ),
        el(
          'span',
          { class: `gauge-text${over ? ' over' : ''}` },
          `병력 ${n}/${cmd}${n === cmd ? ' 가득' : ''} · 전투력 ${this.power().toLocaleString()}`,
        ),
      ),
      this.fillButton(),
      withKey(button('자동 편성', () => this.autoFill(), 'auto'), 'auto'),
      this.recruitButton(),
      this.depthSelect(),
      go,
    );
  }

  /**
   * 출발 깊이 지름길(`startDepths`): 1, 그리고 넘은 관문 바로 다음 깊이. 하나뿐이면(아직 관문을 못 넘었다)
   * 두지 않는다. 고른 것은 계정에 적어 다음에도 그대로다.
   *
   * 처음에는 칩 줄이었다. 깊이는 출정마다 바꾸는 값이 아니라 늘 펼쳐 둘 까닭이 없어 고르기 상자로 접었다.
   * 폰에서는 기본 고르기 창이 떠 손가락으로 고르기 쉽다.
   */
  private depthSelect(): HTMLElement | false {
    const open = startDepths(this.army);
    if (open.length < 2) return false;
    const now = this.startDepth();
    const sel = withKey(
      el('select', { class: 'depth-select', 'aria-label': '출발 깊이', title: '출발 깊이 — 넘은 관문 다음 깊이부터 나설 수 있다' }),
      'depth',
    ) as HTMLSelectElement;
    // 출발 깊이의 전장 특성을 이름에 붙인다(traits.ts). 출정 중에는 편성을 못 바꾸니 떠나기 전에 알아야 한다.
    // 아직 이겨 본 적 없는 깊이(`Army.deepest` 너머)에는 "처음"을 붙인다 — 결과 화면의 진군 카드와 같은 표시다
    // (플레이한 사람: "깬 적이 없는 전장은 알려주면 좋겠어"). 열린 출발 깊이 가운데 그럴 수 있는 것은 막 넘은 관문
    // 바로 다음 하나뿐이다.
    const seed = nextRunSeed(this.army);
    for (const v of open) {
      // 갈림길 구간의 첫 깊이(6·11·…)는 길을 고른 뒤에야 특성이 정해진다 — 나서면 먼저 묻는다(`needsRoute`).
      // 안개가 낀 깊이는 안개만 보인다 — 출정 전에는 척후를 보낼 수 없다.
      const names = isRouteDepth(v) ? ['갈림길'] : traitNames(forecastTraits(traitsForDepth(seed, v, null), false));
      const fresh = v > this.army.deepest ? ' · 처음' : '';
      const o = el('option', { value: String(v) }, `깊이 ${v}${fresh}${names.length > 0 ? ` — ${names.join(' · ')}` : ''}`);
      if (v === now) o.setAttribute('selected', 'selected');
      sel.append(o);
    }
    sel.addEventListener('change', () => {
      this.army.startDepth = Number(sel.value);
      this.focusKey = 'depth';
      this.changed();
      this.render();
    });
    return sel;
  }

  /**
   * 급료가 가장 싼 병사 한 분대(`SQUAD_SIZE`명)를 데려가는 데 모자란 냥. 데려갈 수 있으면 0. 병영의 병사가 한 분대보다
   * 적으면 있는 만큼으로 센다(그때는 모집이나 공짜 징발이 먼저다).
   */
  private squadWageShort(): number {
    // 급료는 병사마다 따로 매겨지므로(`wageFor`) 싼 순으로 한 분대를 고르면 그것이 가장 싼 출정이다.
    const wages = this.army.soldiers.map((x) => wageFor(this.army, [x.uid])).sort((a, b) => a - b);
    const cheapest = wages.slice(0, SQUAD_SIZE).reduce((a, b) => a + b, 0);
    return Math.max(0, cheapest - this.army.gold);
  }

  /** 모집 한 명. 바닥(병사도 돈도 없음)에서는 공짜 징발이다(`recruit`). */
  private recruitButton(): HTMLElement {
    const destitute = isDestitute(this.army);
    const canOne = recruitPlan(this.army, 1).count > 0;
    const one = withKey(
      button(
        destitute ? '징발 무료' : `모집 ${RECRUIT_TROOP_COST}냥`,
        () => this.doRecruit(1),
        `recruit${canOne ? '' : ' off'}`,
      ),
      'recruit',
    );
    if (!canOne) one.setAttribute('aria-disabled', 'true');
    one.title = '민병 한 명을 뽑아 병영에 들인다';
    return one;
  }

  /**
   * 정원까지 모자란 만큼 한꺼번에 뽑아 빈자리에 앉힌다. **정원이 빌 때만** 한 줄로 뜬다 — 찼을 때 "정원
   * 가득"이라고 적힌 못 누르는 단추가 한 줄을 차지하고 있었다. 돈이 모자라면 되는 만큼만 뽑는다고 적는다.
   */
  private fillButton(): HTMLElement | false {
    const short = shortfall(this.army, this.capacity());
    if (short === 0) return false;
    const { count: can, cost } = recruitPlan(this.army, short);
    // 바닥 징발만으로 채우면 값이 0이다. "0냥"보다 무료라고 적는 편이 읽힌다.
    const price = cost > 0 ? `${cost}냥` : '무료';
    const label =
      can === short
        ? `빈자리 ${short}명 뽑아 채우기 · ${price}`
        : can > 0
          ? `빈자리 ${short}명 중 ${can}명 뽑아 채우기 · ${price}`
          : `빈자리 ${short}명 · 돈 모자람`;
    const fill = withKey(
      button(label, () => { if (can > 0) this.doRecruit(can, true); }, `recruit fill${can > 0 ? '' : ' off'}`),
      'recruit-fill',
    );
    if (can === 0) fill.setAttribute('aria-disabled', 'true');
    fill.title = `정원(열린 부대마다 지휘관의 관등만큼)까지 모자란 만큼 민병을 뽑아 빈자리에 바로 넣는다`;
    return fill;
  }

  /** 뽑는다. seat이면 새로 뽑은 병사를 열린 부대 빈자리에 바로 앉힌다(부족분 고용). */
  private doRecruit(n: number, seat = false): void {
    const before = new Set(this.army.soldiers.map((s) => s.uid));
    recruitUpTo(this.army, this.army.culture, n);
    const fresh = this.army.soldiers.filter((s) => !before.has(s.uid)).map((s) => s.uid);
    if (seat) {
      const open = this.squads.map((_, sq) => this.isOpen(sq));
      seatNew(this.squads, open, fresh, this.squads.map((_, sq) => this.sizeOf(sq)));
      this.notice = `${fresh.length}명을 뽑아 빈자리에 앉혔다.`;
      // 다 채우면 빈자리 단추가 사라진다. 키를 남기지 않으면 키보드 포커스가 body로 떨어진다 — 옆 모집 단추가 받는다.
      this.focusKey = shortfall(this.army, this.capacity()) > 0 ? 'recruit-fill' : 'recruit';
    } else if (fresh.length > 0) {
      this.notice = '한 명을 뽑았다. 병영에 들어왔다.';
    }
    this.changed();
    this.render();
  }

  /**
   * 그 밖의 일: 출정의 무게(재건 비용), 편성 비우기, 단신 토벌, 처음부터.
   * 관등·장수 레벨은 내 부대의 지휘관 줄로 옮겼다 — 이 칸은 잘 보지 않았다(플레이한 사람이 짚었다).
   */
  /**
   * 군수품 상점(supplies.ts). 출정 중에는 상점에 갈 수 없으니 여기서 사 둔 것이 곧 들고 나가는 것이다. 판을 시작하기
   * 전(전투 준비)에 그 판에 쓸 것을 세 가지까지 고른다. 장비 상점(`armory`)과 따로 둔 것은 착용품이 아니라 부대가
   * 쓰는 소모품이고, 사는 때가 출정 직전이기 때문이다.
   */
  private supplyShop(): HTMLElement {
    const rows = SUPPLIES.map((s) => {
      const have = supplyCount(this.army, s.id);
      const can = this.army.gold >= s.cost && have < SUPPLY_STOCK_MAX;
      const buy = withKey(
        button(`사기 ${s.cost.toLocaleString()}냥`, () => {
          if (!buySupply(this.army, s.id)) return;
          this.notice = `${s.name}${josa(s.name, '을', '를')} 샀다. 가진 것 ${supplyCount(this.army, s.id)}.`;
          this.anchorKey = `supply:${s.id}`;
          this.focusKey = `supply:${s.id}`;
          this.changed();
          this.render();
        }, `mini${can ? '' : ' off'}`),
        `supply:${s.id}`,
      );
      if (!can) buy.setAttribute('aria-disabled', 'true');
      // 온 문장(`summary`)은 사기 단추의 설명으로도 묶는다 — 카드에는 짧은 효과만 보인다.
      buy.setAttribute('aria-description', s.summary);
      // 단추 이름에 물건 이름을 넣는다 — 값이 같은 횃불과 군량이 둘 다 "사기 120냥"이라 단추 목록에서 갈리지 않았다(리뷰).
      buy.setAttribute('aria-label', `${s.name} 사기 ${s.cost.toLocaleString()}냥`);
      return el(
        'div',
        { class: `supply-card${have > 0 ? ' have' : ''}`, title: s.summary },
        // 32px(칸 그대로) — 40px이면 폰의 두 칸 카드에서 글자가 너무 좁았다.
        supplyIcon(s.id, 32),
        el(
          'div',
          { class: 'supply-txt' },
          el('b', {}, s.name, ' ', el('span', { class: 'dim' }, s.hanja)),
          el('span', { class: 'supply-short' }, s.short),
        ),
        el('div', { class: 'supply-foot' }, el('span', { class: 'supply-have' }, have > 0 ? `${have}개 있음` : '없음'), buy),
      );
    });
    // 여덟 줄의 긴 목록이 화면을 많이 먹었다(플레이한 사람이 짚었다) — 아이콘 카드로 넓은 화면 네 칸, 폰 두 칸.
    return el(
      'section',
      { class: 'panel supply-shop', 'aria-label': '군수품' },
      el('h2', {}, '군수품', el('small', { class: 'dim' }, ' 판마다 세 가지까지 쓴다 · 싸우기 전에 고른다')),
      el('div', { class: 'supply-grid' }, ...rows),
    );
  }

  private others(): HTMLElement {
    return el(
      'section',
      { class: 'panel others' },
      el(
        'div',
        { class: 'others-row' },
        stat('잃으면 재건 비용', `${this.riskCost().toLocaleString()}냥`),
        // 부관도 센다 — 부대 자리에 없을 뿐 함께 나간다.
        stat('맡은 부하장수', String(this.companions.filter((c) => c !== null).length + (this.escort ? 1 : 0))),
        withKey(button('편성 비우기', () => this.clearAll(), 'mini'), 'clear'),
      ),
      // 노획도 전과처럼 철수해야 확정된다(`RunState.pendingGold` — 실패의 무게).
      el('p', { class: 'warn' }, '전과와 노획은 철수해야 확정된다. 장수가 쓰러지면 그 출정의 전과와 노획은 없고, 남은 병력은 퇴각해 빠져나온 병사만 살아 돌아온다. 떠날 때 낸 급료도 돌아오지 않는다.'),
      el(
        'div',
        { class: 'raid-box' },
        el('b', {}, '단신 토벌'),
        el(
          'p',
          { class: 'hint' },
          '장수 혼자 도적떼를 친다. 병사를 걸지 않으므로 전과도 쌓이지 않는다. 두령을 베면 현상금이 붙는다. 져도 잃는 것은 없다 — 번 돈만 없다.',
        ),
        this.raidBands(),
      ),
      // 계정을 지우는 단추는 맨 아래에 둔다. 예전에는 머리의 "장비" 바로 옆이라 잘못 누르기 쉬웠다.
      el('div', { class: 'reset-box' }, el('span', { class: 'dim' }, '계정'), button('처음부터', () => this.onReset(), 'mini danger')),
    );
  }
}

/** 잠긴 부하 부대가 무엇을 하면 열리는가. */
function lockedWhy(sq: number, met: number): string {
  const name = squadName(sq);
  if (sq <= met) return `${name}: 맡을 부하장수가 요양 중이다. 돌아오면 열린다.`;
  const depth = COMPANION_DEPTHS[sq - 1];
  return depth !== undefined ? `${name}: 깊이 ${depth} 관문을 넘어 부하장수가 합류하면 열린다.` : `${name}: 부하장수가 합류하면 열린다.`;
}

/** 병영 줄 세우기. 같은 값이면 명단 순서 — 순서가 늘 같아야 누른 자리를 잃지 않는다. */
function sortSoldiers(army: Army, list: readonly Soldier[], key: SortKey): Soldier[] {
  const base = rosterOrder(list);
  if (key === 'roster') return base;
  const rank = new Map(base.map((s, i) => [s.uid, i]));
  const up = (s: Soldier): number => (promotionOptions(army, s).some((o) => o.ok) ? 1 : 0);
  return [...base].sort((a, b) =>
    key === 'level'
      ? levelProgress(b.xp).level - levelProgress(a.xp).level || rank.get(a.uid)! - rank.get(b.uid)!
      : up(b) - up(a) || rank.get(a.uid)! - rank.get(b.uid)!,
  );
}

/**
 * 옆으로 미는 줄(탭·카드)을 감싼다. 가려진 쪽 끝에 흐림과 ‹ › 를 띄우고, 누르면 그쪽으로 한 뼘 넘긴다.
 * 넘치지 않으면(넓은 화면) 표시도 없다.
 *
 * 감싼 틀은 `min-width: 0`이어야 한다(index.html). 시안에서 처음에는 격자 칸 안의 틀이 카드 줄 길이만큼
 * 늘어나, 줄이 스크롤되지 않고 칸이 넓어져 오른쪽 단추가 잘렸다.
 */
function hinted(scroller: HTMLElement, cls: string, what: string): HTMLElement {
  const edge = (side: 'l' | 'r', glyph: string, label: string, dir: number): HTMLElement => {
    const b = button(glyph, () => scroller.scrollBy({ left: dir * scroller.clientWidth * 0.6, behavior: 'smooth' }), `edge ${side}`);
    b.setAttribute('aria-label', label);
    b.tabIndex = -1;
    return b;
  };
  const wrap = el('div', { class: `hint ${cls}` }, scroller, edge('l', '‹', `앞 ${what} 보기`, -1), edge('r', '›', `뒤 ${what} 보기`, 1));
  scroller.addEventListener('scroll', () => refreshHint(wrap), { passive: true });
  return wrap;
}

function refreshHint(wrap: HTMLElement): void {
  const s = wrap.firstElementChild as HTMLElement | null;
  if (!s) return;
  wrap.classList.toggle('more-l', s.scrollLeft > 2);
  wrap.classList.toggle('more-r', s.scrollLeft + s.clientWidth < s.scrollWidth - 2);
}

/** 고른 탭이 가려져 있으면 끌어온다. 양 끝의 ‹ › 가 덮는 폭만큼 더 당긴다. */
function showSelectedTab(tabs: HTMLElement): void {
  const on = tabs.querySelector<HTMLElement>('[aria-pressed="true"]');
  if (!on) return;
  const pad = 24;
  if (on.offsetLeft - pad < tabs.scrollLeft) tabs.scrollLeft = on.offsetLeft - pad;
  else if (on.offsetLeft + on.offsetWidth + pad > tabs.scrollLeft + tabs.clientWidth) {
    tabs.scrollLeft = on.offsetLeft + on.offsetWidth + pad - tabs.clientWidth;
  }
}

/** 받침에 따라 조사를 고른다(을/를, 이/가, 은/는, 으로/로). 받침이 ㄹ이면 '로'다. */
function josa(word: string, withFinal: string, without: string): string {
  const code = word.charCodeAt(word.length - 1) - 0xac00;
  if (code < 0 || code > 11171) return without;
  const final = code % 28;
  if (withFinal === '으로') return final === 0 || final === 8 ? without : withFinal;
  return final === 0 ? without : withFinal;
}

/** 상세 창의 ‹ › 단추. 끝이면 누를 수 없다는 것을 보조기술에도 알린다. */
function withNav(b: HTMLButtonElement, label: string, off: boolean): HTMLButtonElement {
  b.setAttribute('aria-label', label);
  if (off) {
    b.setAttribute('aria-disabled', 'true');
    b.classList.add('off');
  }
  return b;
}


function stat(label: string, value: string): HTMLElement {
  return el('div', { class: 'stat' }, el('span', { class: 'k' }, label), el('span', { class: 'v' }, value));
}

/** 길잡이 줄의 단추. `badge`가 있으면 금빛 표를 달고, 무슨 표인지 읽어 준다(`hint`). */
function navButton(label: string, onClick: () => void, badge: string, hint: string): HTMLButtonElement {
  const b = button(label, onClick, 'mini');
  if (badge) {
    b.append(el('small', { class: 'nav-badge', 'aria-hidden': 'true' }, badge));
    b.setAttribute('aria-label', `${label} — ${hint}`);
    b.title = hint;
  }
  return b;
}
